import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import SaveIcon from '@mui/icons-material/Save';
import AddIcon from '@mui/icons-material/Add';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import TuneIcon from '@mui/icons-material/Tune';
import LocalOfferIcon from '@mui/icons-material/LocalOffer';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import WbSunnyOutlinedIcon from '@mui/icons-material/WbSunnyOutlined';
import TextureIcon from '@mui/icons-material/Texture';
import RestartAltIcon from '@mui/icons-material/RestartAlt';
import {
    calcModeOptions,
    workTypeOptions,
    tagPresets,
    getDimensionRows,
    getMainPriceText,
    getProductName,
    millingRoundOptions,
    productPricingModeOptions,
    blankPolishingOptions,
    blankSizingModeOptions,
    materialSelectionModeOptions,
    resolveBlankDimensions,
    normalizeMillingPricing,
    normalizeProductPricing,
    normalizeWorkOption,
    createMillingSizeRule,
    createProductSizeRule,
    syncWorkPricingToSharedSizes,
    calcMillingPrice,
    applyMaterialMillingMarkup,
    calcProductPrice,
    calcProductCustomerPrices,
    calcForwardCustomerProfile,
    calcWorkOptionPrice,
    calcWorkOptionsTotal,
    calcProductSizeWeightKg,
    formatMillingDimensions,
    fmtMoney,
    fmtWeightKg,
    toNumber,
    createProportionalDimensionsPatch,
    getWidthToHeightRatio,
} from './adminProduct3DPreview.utils';
import AdminMaterialVariantSelector from './AdminMaterialVariantSelector';
import AdminGraniteTypeSwitcher from './AdminGraniteTypeSwitcher';
import BlankPriceEstimator from '../BlankPriceEstimator';
import {
    DEFAULT_ADMIN_PRODUCT_3D_SCENE_SETTINGS,
    normalizeAdminProduct3DSceneSettings,
} from './adminProduct3DSceneSettings';

const graphicsQualityOptions = [
    { value: 'low', label: 'Низкое — максимум FPS' },
    { value: 'medium', label: 'Среднее — баланс' },
    { value: 'high', label: 'Высокое — чёткие тени' },
];

const editorTabs = [
    { value: 'granite', label: 'Гранит', icon: <TextureIcon fontSize="small" /> },
    { value: 'prices', label: 'Цены', icon: <TuneIcon fontSize="small" /> },
    { value: 'tags', label: 'Теги', icon: <LocalOfferIcon fontSize="small" /> },
    { value: 'main', label: 'Основное', icon: <InfoOutlinedIcon fontSize="small" /> },
    { value: 'settings', label: 'Сцена', icon: <WbSunnyOutlinedIcon fontSize="small" /> },
];

const axisPresetOptions = [
    { value: 'auto', label: 'Авто' },
    { value: 'vertical', label: 'Вертикальный памятник' },
    { value: 'horizontal', label: 'Лежачая плита' },
];

const formatCalculatedBlankPrice = (preview) => {
    const price = toNumber(preview?.blankPrice);
    if (price > 0) {
        return `${preview?.blankEstimated ? '≈ ' : ''}${price.toLocaleString('ru-RU')} ₽`;
    }
    return preview?.blankRequestRequired ? 'Запросить цену' : 'Цена не указана';
};

const getBlankConfidenceLabel = (value) => {
    if (value === 'exact') return 'точная';
    if (value === 'high') return 'высокая';
    if (value === 'medium') return 'средняя';
    if (value === 'low') return 'низкая';
    return 'не определена';
};

const getPriceSourceLabel = (preview) => {
    if (!preview) return '—';

    if (preview.source === 'rule') return preview.label;
    if (preview.source === 'formula') return preview.label;
    if (preview.source === 'manual') return 'Одна ручная цена';
    if (preview.source === 'old') return 'Старая цена';

    return preview.label || '—';
};

const dimensionFallbacks = {
    height: 1000,
    width: 500,
    thickness: 80,
};

const hasCompleteDimensions = (dimensions) => (
    toNumber(dimensions?.height) > 0 &&
    toNumber(dimensions?.width) > 0 &&
    toNumber(dimensions?.thickness) > 0
);

const isSameDimensionsMm = (left, right) => (
    ['height', 'width', 'thickness'].every((key) => {
        const leftValue = Math.round(toNumber(left?.[key]));
        const rightValue = Math.round(toNumber(right?.[key]));

        return leftValue > 0 && rightValue > 0 && leftValue === rightValue;
    })
);

const preventUnsafeNumberKeys = (event) => {
    if (!event?.target || event.target.type !== 'number') return;

    if (['e', 'E', '+', '-'].includes(event.key)) {
        event.preventDefault();
    }
};

const blurNumberInputOnWheel = (event) => {
    const target = event?.target;

    if (target?.type === 'number' && document.activeElement === target) {
        target.blur();
    }
};

const INLINE_POLICY_PROFILES = [
    {
        key: 'subscriber',
        name: 'Подписка',
        productionMarkupPercent: 50,
    },
    {
        key: 'public',
        name: 'Обычная цена',
        productionMarkupPercent: 100,
    },
];

const roundPolicyMoney = (value) => (
    Math.round((Number(value || 0) + Number.EPSILON) * 100) / 100
);

const roundPolicyStep = (value, step) => {
    const safeStep = Number(step) > 0 ? Number(step) : 0.01;
    return roundPolicyMoney(Math.round(Number(value || 0) / safeStep) * safeStep);
};

const normalizeInlinePolicy = (policy = {}) => {
    const profiles = INLINE_POLICY_PROFILES.map((fallback) => {
        const incoming = (Array.isArray(policy?.profiles) ? policy.profiles : [])
            .find((item) => item?.key === fallback.key) || {};

        return {
            ...fallback,
            ...incoming,
            key: fallback.key,
            name: incoming.name || fallback.name,
            productionMarkupPercent: Number.isFinite(Number(incoming.productionMarkupPercent))
                ? Number(incoming.productionMarkupPercent)
                : fallback.productionMarkupPercent,
        };
    });

    return {
        deliveryPercent: Number.isFinite(Number(policy?.deliveryPercent))
            ? Number(policy.deliveryPercent)
            : 10,
        siteSharePercent: Number.isFinite(Number(policy?.siteSharePercent))
            ? Number(policy.siteSharePercent)
            : 6,
        taxReservePercent: Number.isFinite(Number(policy?.taxReservePercent))
            ? Number(policy.taxReservePercent)
            : 10,
        roundTo: Number(policy?.roundTo) > 0 ? Number(policy.roundTo) : 0.01,
        profiles,
    };
};

const calculateInlinePolicyProfile = ({
    blankPrice,
    millingPrice,
    policy,
    profile,
}) => calcForwardCustomerProfile({
    blankPrice,
    worksPrice: millingPrice,
    productionMarkupPercent: profile.productionMarkupPercent,
    pricingPolicy: policy,
    profile,
});

const PricingPolicyInlineBreakdown = ({
    pricingPolicy,
    blankPrice,
    millingPrice,
    mode = 'all',
}) => {
    if (!pricingPolicy) return null;

    const policy = normalizeInlinePolicy(pricingPolicy);
    const profiles = policy.profiles.map((profile) => calculateInlinePolicyProfile({
        blankPrice,
        millingPrice,
        policy,
        profile,
    }));
    const milling = profiles[0]?.milling || {};

    if (!(Number(blankPrice) > 0) && !(Number(millingPrice) > 0)) return null;

    if (mode === 'milling') {
        return (
            <div className="adminProduct3DPreview__policyDistribution is-milling-only">
                <div className="adminProduct3DPreview__policyDistributionHead">
                    <div>
                        <strong>Распределение работы</strong>
                        <span>Цена работы уже конечная для клиента. Сначала выделяем налог, затем сайт; остаток — чистыми исполнителю.</span>
                    </div>
                    <em>
                        клиент → налог {policy.taxReservePercent}% → сайт {policy.siteSharePercent}%
                    </em>
                </div>

                <div className="adminProduct3DPreview__policyMillingFlow">
                    <span><small>Клиент</small><b>{fmtMoney(milling.customerFinal)}</b></span>
                    <span><small>Налог внутри {policy.taxReservePercent}%</small><b>{fmtMoney(milling.taxReserve)}</b></span>
                    <span><small>Без налога</small><b>{fmtMoney(milling.beforeTax)}</b></span>
                    <span><small>Сайт внутри {policy.siteSharePercent}%</small><b>{fmtMoney(milling.siteShare)}</b></span>
                    <span className="is-net"><small>Чистыми исполнителю</small><b>{fmtMoney(milling.partnerNet)}</b></span>
                </div>
            </div>
        );
    }

    return (
        <div className="adminProduct3DPreview__policyDistribution">
            <div className="adminProduct3DPreview__policyDistributionHead">
                <div>
                    <strong>Глобальное распределение денег</strong>
                    <span>Изделие: заготовка → доставка {policy.deliveryPercent}% → наценка → сайт {policy.siteSharePercent}% → налог {policy.taxReservePercent}% последним. Работы уже заданы конечной ценой клиенту.</span>
                </div>
                <em>Не меняет значения выше</em>
            </div>

            <div className="adminProduct3DPreview__policySharedCosts">
                <span><small>Заготовка</small><b>{fmtMoney(profiles[0]?.product?.blankAmount)}</b></span>
                <span><small>Доставка {policy.deliveryPercent}%</small><b>{fmtMoney(profiles[0]?.product?.deliveryAmount)}</b></span>
                <span><small>Закупка всего</small><b>{fmtMoney(profiles[0]?.product?.procurementTotal)}</b></span>
                <span><small>Работы клиенту</small><b>{fmtMoney(milling.customerFinal)}</b></span>
            </div>

            <div className="adminProduct3DPreview__policyProfiles">
                {profiles.map((result) => (
                    <article key={result.profile.key} className={`is-${result.profile.key}`}>
                        <header>
                            <span>{result.profile.key === 'subscriber' ? 'Подписка' : 'Обычная цена'}</span>
                            <strong>{fmtMoney(result.totals.customer)}</strong>
                        </header>
                        <div>
                            <span><small>Сайт {policy.siteSharePercent}%</small><b>{fmtMoney(result.totals.siteShare)}</b></span>
                            <span><small>Налог {policy.taxReservePercent}%</small><b>{fmtMoney(result.totals.taxReserve)}</b></span>
                            <span><small>Производству приходит</small><b>{fmtMoney(result.totals.productionReceived)}</b></span>
                        </div>
                        <small>
                            Работы: клиент {fmtMoney(result.milling.customerFinal)} − налог {fmtMoney(result.milling.taxReserve)} − сайт {fmtMoney(result.milling.siteShare)} = чистыми {fmtMoney(result.milling.partnerNet)}
                        </small>
                        <small>
                            Изделие: закупка {fmtMoney(result.product.procurementTotal)} + прибыль {fmtMoney(result.product.productionProfit)} + сайт {fmtMoney(result.product.siteShare)} + налог {fmtMoney(result.product.taxReserve)} = {fmtMoney(result.product.customerFinal)}
                        </small>
                    </article>
                ))}
            </div>
        </div>
    );
};

const normalizeBlankPickerText = (value) => String(value || '')
    .trim()
    .toLowerCase()
    .replace(/ё/g, 'е');

const resolveBlankPickerOffer = (rule = {}) => {
    const offers = (Array.isArray(rule.supplierOffers) ? rule.supplierOffers : [])
        .filter((offer) => (
            offer?.enabled !== false
            && offer?.confirmed !== false
            && toNumber(offer?.price) > 0
        ));

    if (rule.priceSelectionMode === 'manual' && toNumber(rule.manualPrice) > 0) {
        return { price: toNumber(rule.manualPrice), supplierKey: '', source: 'manual' };
    }

    if (rule.priceSelectionMode === 'supplier' && rule.selectedSupplierKey) {
        const selectedSupplier = normalizeBlankPickerText(rule.selectedSupplierKey);
        const selected = offers.find((offer) => (
            normalizeBlankPickerText(offer?.supplierKey) === selectedSupplier
        ));

        if (selected) {
            return {
                price: toNumber(selected.price),
                supplierKey: String(selected.supplierKey || ''),
                source: 'selected-supplier',
            };
        }
    }

    const cheapest = [...offers].sort((left, right) => (
        toNumber(left.price) - toNumber(right.price)
    ))[0];

    if (cheapest) {
        return {
            price: toNumber(cheapest.price),
            supplierKey: String(cheapest.supplierKey || ''),
            source: 'cheapest-confirmed',
        };
    }

    if (toNumber(rule.manualPrice) > 0) {
        return { price: toNumber(rule.manualPrice), supplierKey: '', source: 'manual-fallback' };
    }

    if (toNumber(rule.price) > 0) {
        return { price: toNumber(rule.price), supplierKey: 'legacy', source: 'legacy' };
    }

    return { price: 0, supplierKey: '', source: 'missing' };
};

const calculateBlankPickerRulePrice = (rule = {}, offer = {}) => {
    const unitPrice = toNumber(offer.price);
    if (!(unitPrice > 0)) return 0;

    const height = toNumber(rule.height);
    const width = toNumber(rule.width);
    const thickness = toNumber(rule.thickness);

    if ((rule.priceUnit || 'piece') === 'squareMeter') {
        return unitPrice * (height * width) / 1_000_000;
    }

    if ((rule.priceUnit || 'piece') === 'cubicMeter') {
        return unitPrice * (height * width * thickness) / 1_000_000_000;
    }

    return unitPrice;
};

const buildBlankPickerCandidates = ({
    catalog,
    blankType,
    polishingType,
    targetDimensions,
    query,
    showAll,
}) => {
    const target = {
        height: Math.round(toNumber(targetDimensions?.height)),
        width: Math.round(toNumber(targetDimensions?.width)),
        thickness: Math.round(toNumber(targetDimensions?.thickness)),
    };
    const normalizedBlankType = normalizeBlankPickerText(blankType || 'stele');
    const normalizedPolishing = normalizeBlankPickerText(polishingType || 'two-side');
    const normalizedQuery = normalizeBlankPickerText(query).replace(/[×хx]/g, ' ');
    const queryParts = normalizedQuery.split(/\s+/).filter(Boolean);

    return (Array.isArray(catalog?.sizeRules) ? catalog.sizeRules : [])
        .map((rule, index) => {
            const dimensions = {
                height: Math.round(toNumber(rule?.height)),
                width: Math.round(toNumber(rule?.width)),
                thickness: Math.round(toNumber(rule?.thickness)),
            };
            const offer = resolveBlankPickerOffer(rule);
            const price = calculateBlankPickerRulePrice(rule, offer);
            const sameKind = (
                normalizeBlankPickerText(rule?.blankType || 'stele') === normalizedBlankType
                && normalizeBlankPickerText(rule?.polishingType || 'two-side') === normalizedPolishing
            );
            const complete = dimensions.height > 0 && dimensions.width > 0 && dimensions.thickness > 0;
            const fits = (
                dimensions.height >= target.height
                && dimensions.width >= target.width
                && dimensions.thickness >= target.thickness
            );
            const exact = (
                dimensions.height === target.height
                && dimensions.width === target.width
                && dimensions.thickness === target.thickness
            );
            const distance = (
                Math.abs(dimensions.height - target.height)
                + Math.abs(dimensions.width - target.width) * 1.4
                + Math.abs(dimensions.thickness - target.thickness) * 4
            );
            const searchable = normalizeBlankPickerText([
                rule?.label,
                dimensions.height,
                dimensions.width,
                dimensions.thickness,
                offer.supplierKey,
            ].filter(Boolean).join(' '));
            const matchesSearch = queryParts.every((part) => searchable.includes(part));

            return {
                key: String(rule?._id || rule?.id || `${dimensions.height}-${dimensions.width}-${dimensions.thickness}-${index}`),
                rule,
                dimensions,
                offer,
                price,
                sameKind,
                complete,
                fits,
                exact,
                distance,
                matchesSearch,
            };
        })
        .filter((item) => (
            item.sameKind
            && item.complete
            && item.price > 0
            && item.matchesSearch
            && (showAll || item.fits)
        ))
        .sort((left, right) => {
            if (left.exact !== right.exact) return left.exact ? -1 : 1;
            if (left.fits !== right.fits) return left.fits ? -1 : 1;
            if (left.distance !== right.distance) return left.distance - right.distance;
            return left.price - right.price;
        })
        .slice(0, 100);
};

const AdminProduct3DInfoEditor = ({
    product,
    form,
    saving,
    saveStatus,
    isDirty,
    initialForm,
    realDimensions,
    sceneDimensions,
    pricingDimensions = null,
    showDimensions,
    sceneSettings,
    blankPriceCatalogs = [],
    pricingPolicy = null,
    modelMaterials = [],
    activeEditorTab: controlledActiveEditorTab,
    onEditorTabChange,
    priceModeTab: controlledPriceModeTab,
    onPriceModeTabChange,
    onSelectMaterialVariant,
    onAddMaterialVariant,
    onSetDefaultMaterialVariant,
    onRemoveMaterialVariant,
    onToggleDimensions,
    onSceneSettingChange,
    onChange,
    onAddTagPreset,
    onReset,
    onSave,
}) => {
    const normalizeEditorTab = useCallback((value) => (
        ['granite', 'prices', 'tags', 'main', 'settings'].includes(String(value || ''))
            ? String(value)
            : 'prices'
    ), []);
    const [internalActiveTab, setInternalActiveTab] = useState('prices');
    const hasControlledActiveTab = (
        typeof controlledActiveEditorTab === 'string'
        && ['granite', 'prices', 'tags', 'main', 'settings'].includes(controlledActiveEditorTab)
    );
    const activeTab = hasControlledActiveTab
        ? normalizeEditorTab(controlledActiveEditorTab)
        : internalActiveTab;
    const setActiveTab = useCallback((nextTab) => {
        const safeTab = normalizeEditorTab(nextTab);

        if (!hasControlledActiveTab) {
            setInternalActiveTab(safeTab);
        }

        onEditorTabChange?.(safeTab);
    }, [hasControlledActiveTab, normalizeEditorTab, onEditorTabChange]);

    const normalizePriceModeTab = useCallback((value) => (
        value === 'milling' || value === 'work' ? 'milling' : 'product'
    ), []);
    const [internalPriceModeTab, setInternalPriceModeTab] = useState('product');
    const hasControlledPriceModeTab = (
        controlledPriceModeTab === 'milling'
        || controlledPriceModeTab === 'work'
        || controlledPriceModeTab === 'product'
    );
    const priceModeTab = hasControlledPriceModeTab
        ? normalizePriceModeTab(controlledPriceModeTab)
        : internalPriceModeTab;
    const setPriceModeTab = useCallback((nextMode) => {
        const normalizedMode = normalizePriceModeTab(nextMode);

        if (!hasControlledPriceModeTab) {
            setInternalPriceModeTab(normalizedMode);
        }

        onPriceModeTabChange?.(normalizedMode);
    }, [hasControlledPriceModeTab, normalizePriceModeTab, onPriceModeTabChange]);
    const [workBreakdownOpen, setWorkBreakdownOpen] = useState(false);
    const [productBreakdownOpen, setProductBreakdownOpen] = useState(false);
    const [productSizeRulesOpen, setProductSizeRulesOpen] = useState(() => (
        Array.isArray(form?.productPricing?.sizeRules)
        && form.productPricing.sizeRules.length > 0
    ));
    const [proportionalResizeEnabled, setProportionalResizeEnabled] = useState(false);
    const [blankPickerOpen, setBlankPickerOpen] = useState(false);
    const [blankPickerQuery, setBlankPickerQuery] = useState('');
    const [blankPickerShowAll, setBlankPickerShowAll] = useState(false);
    const proportionalEditRef = useRef(null);

    useEffect(() => {
        if (!blankPickerOpen) return undefined;

        const handleKeyDown = (event) => {
            if (event.key !== 'Escape') return;
            event.preventDefault();
            event.stopPropagation();
            setBlankPickerOpen(false);
        };

        document.addEventListener('keydown', handleKeyDown, true);
        return () => document.removeEventListener('keydown', handleKeyDown, true);
    }, [blankPickerOpen]);

    const activeCalcMode = useMemo(() => {
        return calcModeOptions.find((item) => item.value === form.workCalcMode) || calcModeOptions[0];
    }, [form.workCalcMode]);

    const realDimensionRows = useMemo(() => {
        return getDimensionRows(realDimensions);
    }, [realDimensions]);

    const sceneDimensionRows = useMemo(() => {
        return getDimensionRows(sceneDimensions);
    }, [sceneDimensions]);

    const millingPricing = useMemo(() => {
        return normalizeMillingPricing(form.millingPricing, product, pricingPolicy || {});
    }, [form.millingPricing, product, pricingPolicy]);

    const productPricing = useMemo(() => {
        const normalized = normalizeProductPricing(
            form.productPricing,
            product,
            blankPriceCatalogs
        );

        if (normalized.mode !== 'blankPlusMilling') return normalized;

        return {
            ...normalized,
            // В новом режиме округление единое для всех товаров и берётся
            // из глобальной ценовой политики. Локальное поле оставляем в данных
            // только для совместимости со старыми сохранёнными товарами.
            roundTo: toNumber(pricingPolicy?.roundTo) || 0.01,
        };
    }, [
        form.productPricing,
        product,
        blankPriceCatalogs,
        pricingPolicy?.roundTo,
    ]);

    const activeMaterialKey = String(
        form.activeMaterialKey
        || form.defaultMaterialKey
        || ''
    );
    const defaultMaterialKey = String(form.defaultMaterialKey || '');
    const materialVariants = Array.isArray(form.materialPricingVariants)
        ? form.materialPricingVariants
        : [];
    const canonicalMillingMaterialKey = materialVariants.some((variant) => (
        String(variant?.materialKey || '') === 'gabbro-diabase'
    ))
        ? 'gabbro-diabase'
        : (defaultMaterialKey || activeMaterialKey || 'gabbro-diabase');
    const isCanonicalMillingMaterial = activeMaterialKey === canonicalMillingMaterialKey;
    const activeBlankMaterialCatalog = blankPriceCatalogs.find((item) => (
        String(item?.key || '') === String(productPricing.blankMaterialKey || activeMaterialKey)
    )) || null;
    const canonicalMillingMaterialCatalog = blankPriceCatalogs.find((item) => (
        String(item?.key || '') === canonicalMillingMaterialKey
    )) || null;
    const activeMaterialDisplayName = String(
        activeBlankMaterialCatalog?.name
        || productPricing.blankMaterialName
        || activeMaterialKey
        || 'Материал'
    );
    const canonicalMillingMaterialName = String(
        canonicalMillingMaterialCatalog?.name
        || canonicalMillingMaterialKey
        || 'Габбро-диабаз'
    );
    const allowLegacyPriceFallback = (
        !activeMaterialKey
        || !defaultMaterialKey
        || activeMaterialKey === defaultMaterialKey
    );
    const productTestDimensions = (
        pricingDimensions
        || productPricing.testDimensions
        || millingPricing.testDimensions
        || productPricing.baseDimensions
        || millingPricing.baseDimensions
    );
    // Один текущий размер используется и изделием, и всеми работами.
    const testDimensions = productTestDimensions;
    const legacyProductFallback = allowLegacyPriceFallback
        ? product?.price
        : 0;
    const legacyMillingFallback = allowLegacyPriceFallback
        ? form.workPrice
        : 0;

    const effectiveWorkOptions = useMemo(() => {
        const source = Array.isArray(form.workOptions) ? form.workOptions : [];
        const currentKey = String(form.workKey || form.workType || 'milling');
        const currentDraft = normalizeWorkOption({
            key: currentKey,
            name: form.workName,
            enabled: form.workEnabled,
            workType: form.workType,
            calcMode: form.workCalcMode,
            price: form.workPrice,
            pricePerMeter: form.workPricePerMeter,
            pricePerSquareMeter: form.workPricePerSquareMeter,
            pricePerCubicMeter: form.workPricePerCubicMeter,
            note: form.workNote,
            pricing: syncWorkPricingToSharedSizes(
                millingPricing,
                productPricing.sizeRules,
                productPricing.baseDimensions
            ),
        }, product);
        let found = false;
        const next = source.map((item, index) => {
            const normalized = normalizeWorkOption(item, product, index);
            if (String(normalized.key) !== currentKey) return normalized;
            found = true;
            return currentDraft;
        });

        if (!found) next.push(currentDraft);
        return next;
    }, [
        form.workOptions,
        form.workKey,
        form.workName,
        form.workEnabled,
        form.workType,
        form.workCalcMode,
        form.workPrice,
        form.workPricePerMeter,
        form.workPricePerSquareMeter,
        form.workPricePerCubicMeter,
        form.workNote,
        millingPricing,
        productPricing.sizeRules,
        productPricing.baseDimensions,
        product,
    ]);

    const currentWorksSummary = useMemo(() => (
        calcWorkOptionsTotal(
            effectiveWorkOptions,
            productTestDimensions,
            pricingPolicy || {},
            {
                millingPricingOverride: millingPricing,
                millingFallbackPrice: legacyMillingFallback,
                materialCatalog: activeBlankMaterialCatalog,
            }
        )
    ), [
        effectiveWorkOptions,
        productTestDimensions,
        pricingPolicy,
        millingPricing,
        legacyMillingFallback,
        activeBlankMaterialCatalog,
    ]);

    const productTestPreview = useMemo(() => {
        return calcProductPrice(
            {
                ...productPricing,
                sizeRules: [],
            },
            productTestDimensions,
            legacyProductFallback,
            {
                blankPriceCatalogs,
                millingPricing,
                millingFallbackPrice: legacyMillingFallback,
                pricingPolicy,
                workOptions: effectiveWorkOptions,
            }
        );
    }, [
        productPricing,
        productTestDimensions,
        legacyProductFallback,
        blankPriceCatalogs,
        millingPricing,
        legacyMillingFallback,
        pricingPolicy,
        effectiveWorkOptions,
    ]);

    const getCustomerPricesForPreview = useCallback((preview) => (
        calcProductCustomerPrices({
            blankPrice: preview?.blankPrice,
            millingPrice: preview?.millingPrice,
            worksPrice: preview?.worksPrice,
            fallbackPrice: preview?.price,
            pricingPolicy: pricingPolicy || {},
        })
    ), [pricingPolicy]);

    const productTestCustomerPrices = useMemo(() => (
        getCustomerPricesForPreview(productTestPreview)
    ), [getCustomerPricesForPreview, productTestPreview]);

    const testPreview = useMemo(() => {
        return applyMaterialMillingMarkup(
            calcMillingPrice(
                millingPricing,
                testDimensions,
                legacyMillingFallback,
                pricingPolicy || {}
            ),
            activeBlankMaterialCatalog,
            pricingPolicy || {}
        );
    }, [
        millingPricing,
        testDimensions,
        legacyMillingFallback,
        pricingPolicy,
        activeBlankMaterialCatalog,
    ]);

    const standardPreview = useMemo(() => {
        return applyMaterialMillingMarkup(
            calcMillingPrice(
                millingPricing,
                millingPricing.baseDimensions,
                legacyMillingFallback,
                pricingPolicy || {}
            ),
            activeBlankMaterialCatalog,
            pricingPolicy || {}
        );
    }, [
        millingPricing,
        legacyMillingFallback,
        pricingPolicy,
        activeBlankMaterialCatalog,
    ]);

    const materialPriceSummaryByKey = useMemo(() => {
        const variants = Array.isArray(form.materialPricingVariants)
            ? form.materialPricingVariants
            : [];

        return variants.reduce((result, variant) => {
            const materialKey = String(variant?.materialKey || '').trim();

            if (!materialKey) return result;

            const variantMillingPricing = normalizeMillingPricing(
                variant?.millingPricing,
                product
            );
            const variantProductPricing = normalizeProductPricing(
                variant?.productPricing,
                product,
                blankPriceCatalogs
            );
            const isDefaultVariant = (
                variant?.isDefault === true
                || materialKey === form.defaultMaterialKey
            );
            const variantMillingFallback = isDefaultVariant
                ? form.workPrice
                : 0;
            const variantProductFallback = isDefaultVariant
                ? product?.price
                : 0;
            const variantMaterialCatalog = blankPriceCatalogs.find((item) => (
                String(item?.key || '') === materialKey
            )) || null;
            const millingPreview = applyMaterialMillingMarkup(
                calcMillingPrice(
                    variantMillingPricing,
                    productTestDimensions,
                    variantMillingFallback,
                    pricingPolicy || {}
                ),
                variantMaterialCatalog,
                pricingPolicy || {}
            );
            const productPreview = calcProductPrice(
                variantProductPricing,
                productTestDimensions,
                variantProductFallback,
                {
                    blankPriceCatalogs,
                    millingPricing: variantMillingPricing,
                    millingFallbackPrice: variantMillingFallback,
                    pricingPolicy,
                    workOptions: effectiveWorkOptions,
                }
            );

            const customerPrices = getCustomerPricesForPreview(productPreview);

            result[materialKey] = {
                // Карточка гранита показывает обычную клиентскую цену,
                // а не техническую сумму до глобального распределения.
                productPrice: customerPrices.publicPrice,
                subscriberPrice: customerPrices.subscriberPrice,
                publicPrice: customerPrices.publicPrice,
                millingPrice: millingPreview.price,
                productSource: productPreview.source,
                millingSource: millingPreview.source,
                productEstimated: productPreview.blankEstimated === true,
                productRequestRequired: productPreview.blankRequestRequired === true,
                productConfidence: productPreview.blankConfidence || '',
                productSupplierKey: productPreview.blankSupplierKey || '',
                productReferenceMaterialName: productPreview.blankReferenceMaterialName || '',
            };

            return result;
        }, {});
    }, [
        form.materialPricingVariants,
        form.workPrice,
        product,
        product?.price,
        productTestDimensions,
        blankPriceCatalogs,
        pricingPolicy,
        getCustomerPricesForPreview,
        effectiveWorkOptions,
    ]);

    const millingCurrentWeightKg = useMemo(() => {
        return calcProductSizeWeightKg(
            testDimensions,
            productPricing.materialDensityKgM3
        );
    }, [testDimensions, productPricing.materialDensityKgM3]);

    const safeSceneSettings = normalizeAdminProduct3DSceneSettings(
        sceneSettings
    );

    const sceneSettingsAreDefault = (
        safeSceneSettings.graphicsQuality
            === DEFAULT_ADMIN_PRODUCT_3D_SCENE_SETTINGS.graphicsQuality
        && safeSceneSettings.dimensionLabelScale
            === DEFAULT_ADMIN_PRODUCT_3D_SCENE_SETTINGS.dimensionLabelScale
    );

    const resetSceneSettingsToDefault = () => {
        onSceneSettingChange?.(
            'graphicsQuality',
            DEFAULT_ADMIN_PRODUCT_3D_SCENE_SETTINGS.graphicsQuality
        );
        onSceneSettingChange?.(
            'dimensionLabelScale',
            DEFAULT_ADMIN_PRODUCT_3D_SCENE_SETTINGS.dimensionLabelScale
        );
    };

    const updateMillingPricing = (patch) => {
        const nextPricing = {
            ...millingPricing,
            ...patch,
            mode: 'areaThicknessForward',
            complexityMultiplier: 1,
        };

        onChange('millingPricing', nextPricing);

        // workPrice остаётся legacy-полем, но держим его равным общей
        // базовой цене фрезеровки, чтобы старые участки проекта не расходились.
        if (form.workType === 'milling' && Object.prototype.hasOwnProperty.call(patch, 'basePrice')) {
            onChange('workPrice', patch.basePrice);
        }
    };

    const updateProductPricing = (patch) => {
        const nextPricing = {
            ...productPricing,
            ...patch,
        };
        const recalculatesFormula = [
            'basePrice',
            'baseDimensions',
            'setupPercent',
            'roundTo',
            'materialDensityKgM3',
            'mode',
            'blankMaterialKey',
            'blankType',
            'blankPolishingType',
            'blankSupplierKey',
            'allowEstimatedBlankPrice',
            'blankPriceOverride',
            'blankSizingMode',
            'manualBlankDimensions',
            'blankHeightStepMm',
            'blankWidthStepMm',
            'blankThicknessStepMm',
            'blankMinimumThicknessMm',
            'manualPrice',
        ].some((key) => Object.prototype.hasOwnProperty.call(patch, key));

        if (recalculatesFormula && Array.isArray(nextPricing.sizeRules)) {
            nextPricing.sizeRules = nextPricing.sizeRules.map((rule) => {
                const preview = calcProductPrice(
                    {
                        ...nextPricing,
                        enabled: true,
                        sizeRules: [],
                    },
                    rule,
                    product?.price,
                    {
                        blankPriceCatalogs,
                        millingPricing,
                        millingFallbackPrice: form.workPrice,
                        pricingPolicy,
                        workOptions: effectiveWorkOptions,
                    }
                );

                const customerPrices = getCustomerPricesForPreview(preview);

                return {
                    ...rule,
                    price: preview.price,
                    subscriberPrice: customerPrices.subscriberPrice,
                    publicPrice: customerPrices.publicPrice,
                    weightKg: rule.weightManual
                        ? rule.weightKg
                        : calcProductSizeWeightKg(rule, nextPricing.materialDensityKgM3),
                };
            });
        }

        onChange('productPricing', nextPricing);
    };

    const updateProductTestDimensions = (patch) => {
        const nextDimensions = {
            ...productTestDimensions,
            ...patch,
            unit: 'mm',
        };

        updateProductPricing({ testDimensions: nextDimensions });
        updateMillingPricing({ testDimensions: nextDimensions });
    };

    const resetProductTestToStandard = () => {
        updateProductTestDimensions(productPricing.baseDimensions);
    };

    const setProductTestAsStandard = () => {
        if (!hasCompleteDimensions(productTestDimensions)) return;

        const nextBaseDimensions = {
            height: toNumber(productTestDimensions.height),
            width: toNumber(productTestDimensions.width),
            thickness: toNumber(productTestDimensions.thickness),
            unit: 'mm',
        };
        const automaticWeightKg = calcProductSizeWeightKg(
            nextBaseDimensions,
            productPricing.materialDensityKgM3
        );
        let matchingRuleWasFound = false;
        const nextRules = [];

        productPricing.sizeRules.forEach((rule) => {
            if (!isSameDimensionsMm(rule, nextBaseDimensions)) {
                nextRules.push({ ...rule, isBase: false });
                return;
            }

            // Один размер должен присутствовать в списке только один раз.
            // Если старые дубли уже есть, при назначении базы оставляем первый.
            if (matchingRuleWasFound) return;

            matchingRuleWasFound = true;

            const keepManualWeight = (
                rule.weightManual === true &&
                toNumber(rule.weightKg) > 0
            );

            nextRules.push({
                ...rule,
                isBase: true,
                label: `${nextBaseDimensions.height}×${nextBaseDimensions.width}×${nextBaseDimensions.thickness}`,
                height: nextBaseDimensions.height,
                width: nextBaseDimensions.width,
                thickness: nextBaseDimensions.thickness,
                price: toNumber(productTestPreview.price) || toNumber(productPricing.basePrice),
                subscriberPrice: productTestCustomerPrices.subscriberPrice,
                publicPrice: productTestCustomerPrices.publicPrice,
                weightKg: keepManualWeight
                    ? rule.weightKg
                    : automaticWeightKg,
                weightManual: keepManualWeight,
                enabled: true,
            });
        });

        if (!matchingRuleWasFound) {
            nextRules.push(
                createProductSizeRule(
                    { ...nextBaseDimensions, isBase: true },
                    productTestPreview.price || productPricing.basePrice,
                    automaticWeightKg,
                    false,
                    productTestCustomerPrices
                )
            );
        }

        updateProductPricing({
            enabled: true,
            baseDimensions: nextBaseDimensions,
            testDimensions: nextBaseDimensions,
            sizeRules: nextRules,
        });
        updateMillingPricing({
            baseDimensions: nextBaseDimensions,
            testDimensions: nextBaseDimensions,
            sizeRules: syncWorkPricingToSharedSizes(
                millingPricing,
                nextRules,
                nextBaseDimensions
            ).sizeRules,
        });
        setProductSizeRulesOpen(true);
    };

    const startProportionalEdit = (scope, dimensions) => {
        if (!proportionalResizeEnabled) {
            proportionalEditRef.current = null;
            return;
        }

        const widthToHeightRatio = getWidthToHeightRatio(dimensions);

        proportionalEditRef.current = widthToHeightRatio
            ? { scope, widthToHeightRatio }
            : null;
    };

    const stopProportionalEdit = (scope) => {
        if (proportionalEditRef.current?.scope === scope) {
            proportionalEditRef.current = null;
        }
    };

    const getProportionalRatio = (scope, dimensions) => {
        if (proportionalEditRef.current?.scope === scope) {
            return proportionalEditRef.current.widthToHeightRatio;
        }

        return getWidthToHeightRatio(dimensions);
    };

    const toggleProportionalResize = (enabled) => {
        proportionalEditRef.current = null;
        setProportionalResizeEnabled(enabled);
    };

    const finishMainDimensionEdit = (
        scope,
        key,
        dimensions,
        fallbackDimensions,
        updateDimension
    ) => {
        stopProportionalEdit(scope);

        if (toNumber(dimensions?.[key]) > 0) return;

        const fallback = toNumber(fallbackDimensions?.[key]) || dimensionFallbacks[key];

        updateDimension(key, fallback);
    };

    const updateProductTestDimension = (key, value) => {
        const scope = 'product-test-dimensions';

        updateProductTestDimensions(
            createProportionalDimensionsPatch(
                productTestDimensions,
                key,
                value,
                proportionalResizeEnabled,
                getProportionalRatio(scope, productTestDimensions)
            )
        );
    };

    const addProductTestSizeToList = () => {
        if (!hasCompleteDimensions(productTestDimensions)) return;

        const nextDimensions = {
            height: toNumber(productTestDimensions.height),
            width: toNumber(productTestDimensions.width),
            thickness: toNumber(productTestDimensions.thickness),
            unit: 'mm',
        };
        const automaticWeightKg = calcProductSizeWeightKg(
            nextDimensions,
            productPricing.materialDensityKgM3
        );
        let matchingRuleWasFound = false;
        const nextRules = [];

        productPricing.sizeRules.forEach((rule) => {
            if (!isSameDimensionsMm(rule, nextDimensions)) {
                nextRules.push(rule);
                return;
            }

            if (matchingRuleWasFound) return;

            matchingRuleWasFound = true;

            const keepManualWeight = (
                rule.weightManual === true &&
                toNumber(rule.weightKg) > 0
            );

            nextRules.push({
                ...rule,
                label: `${nextDimensions.height}×${nextDimensions.width}×${nextDimensions.thickness}`,
                height: nextDimensions.height,
                width: nextDimensions.width,
                thickness: nextDimensions.thickness,
                price: toNumber(productTestPreview.price) || toNumber(productPricing.basePrice),
                subscriberPrice: productTestCustomerPrices.subscriberPrice,
                publicPrice: productTestCustomerPrices.publicPrice,
                weightKg: keepManualWeight
                    ? rule.weightKg
                    : automaticWeightKg,
                weightManual: keepManualWeight,
                enabled: true,
            });
        });

        if (!matchingRuleWasFound) {
            nextRules.push(
                createProductSizeRule(
                    nextDimensions,
                    productTestPreview.price || productPricing.basePrice,
                    automaticWeightKg,
                    false,
                    productTestCustomerPrices
                )
            );
        }

        updateProductPricing({
            enabled: true,
            sizeRules: nextRules,
        });
        setProductSizeRulesOpen(true);
    };

    const updateProductSizeRule = (index, key, value) => {
        const nextRules = productPricing.sizeRules.map((rule, ruleIndex) => {
            if (ruleIndex !== index) return rule;

            const scope = `product-size-rule-${rule._clientId || index}`;
            const updatedRule = {
                ...rule,
                ...createProportionalDimensionsPatch(
                    rule,
                    key,
                    value,
                    proportionalResizeEnabled,
                    getProportionalRatio(scope, rule)
                ),
            };

            if (['height', 'width', 'thickness'].includes(key)) {
                updatedRule.label = `${toNumber(updatedRule.height) || 0}×${toNumber(updatedRule.width) || 0}×${toNumber(updatedRule.thickness) || 0}`;

                const preview = calcProductPrice(
                    {
                        ...productPricing,
                        enabled: true,
                        sizeRules: [],
                    },
                    updatedRule,
                    product?.price,
                    {
                        blankPriceCatalogs,
                        millingPricing,
                        millingFallbackPrice: form.workPrice,
                        pricingPolicy,
                        workOptions: effectiveWorkOptions,
                    }
                );
                const customerPrices = getCustomerPricesForPreview(preview);

                updatedRule.price = preview.price;
                updatedRule.subscriberPrice = customerPrices.subscriberPrice;
                updatedRule.publicPrice = customerPrices.publicPrice;

                if (!updatedRule.weightManual) {
                    updatedRule.weightKg = calcProductSizeWeightKg(updatedRule, productPricing.materialDensityKgM3);
                }
            }

            if (key === 'weightKg') {
                updatedRule.weightManual = true;
            }

            if (key === 'weightManual' && value === false) {
                updatedRule.weightKg = calcProductSizeWeightKg(updatedRule, productPricing.materialDensityKgM3);
            }

            return updatedRule;
        });

        updateProductPricing({
            enabled: true,
            sizeRules: nextRules,
        });
        updateMillingPricing({
            sizeRules: syncWorkPricingToSharedSizes(
                millingPricing,
                nextRules,
                productPricing.baseDimensions
            ).sizeRules,
        });
    };

    const showProductRuleOnScene = (rule) => {
        updateProductTestDimensions({
            height: toNumber(rule.height) || 1000,
            width: toNumber(rule.width) || 500,
            thickness: toNumber(rule.thickness) || 80,
        });
    };

    // Размеры изделия и работ общие. Полное удаление должно быть явным:
    // оно убирает размер из изделия, текущей и остальных работ после сохранения.
    const removeProductSizeRule = (index) => {
        const targetRule = productPricing.sizeRules[index];
        if (!targetRule || saving) return;

        const label = formatMillingDimensions(targetRule);
        if (!window.confirm(
            `Удалить общий размер ${label}?\n\n` +
            'Он исчезнет из вариантов ГОТОВОГО ИЗДЕЛИЯ и ВСЕХ РАБОТ.\n' +
            'Если требуется убрать только фрезеровку, нажми «Убрать из работы».\n\n' +
            'Изменение вступит в силу после кнопки «Сохранить».'
        )) return;

        const nextRules = productPricing.sizeRules.filter((_, ruleIndex) => ruleIndex !== index);
        updateProductPricing({ sizeRules: nextRules });
        updateMillingPricing({
            sizeRules: syncWorkPricingToSharedSizes(
                millingPricing,
                nextRules,
                productPricing.baseDimensions
            ).sizeRules,
        });
    };

    const duplicateProductSizeRule = (index) => {
        const sourceRule = productPricing.sizeRules[index];

        if (!sourceRule) return;

        updateProductPricing({
            enabled: true,
            sizeRules: [
                ...productPricing.sizeRules,
                {
                    ...sourceRule,
                    _clientId: `${Date.now()}-${Math.random().toString(16).slice(2)}`,
                    label: `${sourceRule.label || 'Размер'} копия`,
                },
            ],
        });
    };

    const updateBaseDimensions = (patch) => {
        const nextBase = {
            ...productPricing.baseDimensions,
            ...patch,
            unit: 'mm',
        };
        updateProductPricing({ baseDimensions: nextBase });
        updateMillingPricing({ baseDimensions: nextBase });
    };

    const updateTestDimensions = (patch) => {
        updateProductTestDimensions(patch);
    };

    const updateTestDimension = (key, value) => {
        const scope = 'work-test-dimensions';

        updateTestDimensions(
            createProportionalDimensionsPatch(
                testDimensions,
                key,
                value,
                proportionalResizeEnabled,
                getProportionalRatio(scope, testDimensions)
            )
        );
    };

    const resetTestToStandard = () => {
        updateProductTestDimensions(productPricing.baseDimensions);
    };

    const copyProductBaseToMilling = () => {
        if (!hasCompleteDimensions(productPricing.baseDimensions)) return;
        updateProductTestDimensions(productPricing.baseDimensions);
    };

    const setTestAsStandard = () => {
        if (!hasCompleteDimensions(testDimensions)) return;
        setProductTestAsStandard();
        updateMillingPricing({
            enabled: form.workEnabled === true,
            baseDimensions: {
                height: toNumber(testDimensions.height),
                width: toNumber(testDimensions.width),
                thickness: toNumber(testDimensions.thickness),
                unit: 'mm',
            },
            testDimensions: {
                height: toNumber(testDimensions.height),
                width: toNumber(testDimensions.width),
                thickness: toNumber(testDimensions.thickness),
                unit: 'mm',
            },
        });
    };

    const getCurrentWorkRule = (sharedRule) => {
        const synced = syncWorkPricingToSharedSizes(
            millingPricing,
            productPricing.sizeRules,
            productPricing.baseDimensions
        );

        return synced.sizeRules.find((rule) => (
            (sharedRule?.sizeKey && rule?.sizeKey === sharedRule.sizeKey)
            || isSameDimensionsMm(rule, sharedRule)
        )) || null;
    };

    // Убрать/вернуть конкретный общий размер только для выбранной работы.
    // Само изделие и прочие работы не меняются.
    const setCurrentWorkSizeEnabled = (sharedRule, enabled) => {
        if (saving || !sharedRule) return;
        const label = formatMillingDimensions(sharedRule);
        if (!enabled && !window.confirm(
            `Убрать ${label} только из работы «${form.workName || 'Фрезеровка'}»?\n\n` +
            'Готовое изделие и остальные работы останутся без изменений.\n' +
            'Для записи изменения нажми «Сохранить».'
        )) return;

        const synced = syncWorkPricingToSharedSizes(
            millingPricing,
            productPricing.sizeRules,
            productPricing.baseDimensions
        );
        const nextRules = synced.sizeRules.map((rule) => (
            ((sharedRule.sizeKey && rule.sizeKey === sharedRule.sizeKey)
                || isSameDimensionsMm(rule, sharedRule))
                ? { ...rule, enabled: !!enabled }
                : rule
        ));
        updateMillingPricing({ sizeRules: nextRules });
    };

    const updateCurrentWorkSizePrice = (sharedRule, value) => {
        const synced = syncWorkPricingToSharedSizes(
            millingPricing,
            productPricing.sizeRules,
            productPricing.baseDimensions
        );
        const nextRules = synced.sizeRules.map((rule) => (
            ((sharedRule?.sizeKey && rule?.sizeKey === sharedRule.sizeKey) || isSameDimensionsMm(rule, sharedRule))
                ? { ...rule, price: value, enabled: true }
                : rule
        ));

        updateMillingPricing({
            enabled: true,
            sizeRules: nextRules,
        });
    };

    const addTestSizeToList = () => {
        addProductTestSizeToList();
    };

    const showRuleOnScene = (rule) => {
        updateProductTestDimensions({
            height: toNumber(rule.height) || 1000,
            width: toNumber(rule.width) || 500,
            thickness: toNumber(rule.thickness) || 80,
        });
    };

    const applyWorkOptionToEditor = (work) => {
        const normalized = normalizeWorkOption(work, product);
        const syncedPricing = syncWorkPricingToSharedSizes(
            normalized.pricing,
            productPricing.sizeRules,
            productPricing.baseDimensions
        );

        onChange('workKey', normalized.key);
        onChange('workName', normalized.name);
        onChange('workEnabled', normalized.enabled !== false);
        onChange('workType', normalized.workType);
        onChange('workCalcMode', normalized.calcMode);
        onChange('workPrice', normalized.price || '');
        onChange('workPricePerMeter', normalized.pricePerMeter || '');
        onChange('workPricePerSquareMeter', normalized.pricePerSquareMeter || '');
        onChange('workPricePerCubicMeter', normalized.pricePerCubicMeter || '');
        onChange('workNote', normalized.note || '');
        onChange('millingPricing', syncedPricing);
    };

    const selectWorkOption = (workKey) => {
        const list = effectiveWorkOptions;
        const target = list.find((item) => String(item.key) === String(workKey));
        if (!target) return;
        onChange('workOptions', list);
        applyWorkOptionToEditor(target);
    };

    const addCustomWorkOption = () => {
        const key = `custom-${Date.now()}`;
        const nextWork = normalizeWorkOption({
            key,
            name: 'Дополнительная работа',
            enabled: true,
            workType: 'custom',
            calcMode: 'fixed',
            price: 0,
            pricing: syncWorkPricingToSharedSizes(
                { enabled: false, basePrice: 0, sizeRules: [] },
                productPricing.sizeRules,
                productPricing.baseDimensions
            ),
            sortOrder: effectiveWorkOptions.length,
        }, product);
        const next = [...effectiveWorkOptions, nextWork];
        onChange('workOptions', next);
        applyWorkOptionToEditor(nextWork);
    };

    const removeCurrentWorkOption = () => {
        const currentKey = String(form.workKey || '');
        const next = effectiveWorkOptions.filter((item) => String(item.key) !== currentKey);
        onChange('workOptions', next);

        if (next.length) {
            applyWorkOptionToEditor(next[0]);
            return;
        }

        applyWorkOptionToEditor({
            key: 'milling',
            name: 'Фрезеровка',
            enabled: false,
            workType: 'milling',
            calcMode: 'fixed',
            price: 0,
            pricing: syncWorkPricingToSharedSizes(
                { enabled: false, basePrice: 0, sizeRules: [] },
                productPricing.sizeRules,
                productPricing.baseDimensions
            ),
        });
    };

    const renderProportionalResizeSwitch = () => (
        <label className="adminProduct3DPreview__switchRow adminProduct3DPreview__switchRow--proportional">
            <span>
                <strong>Пропорционально</strong>
                <small>Высота и ширина меняются вместе. Толщина остаётся отдельной.</small>
            </span>

            <input
                type="checkbox"
                checked={proportionalResizeEnabled}
                onChange={(event) => toggleProportionalResize(event.target.checked)}
                disabled={saving}
            />
        </label>
    );

    const renderTabs = () => (
        <div className="adminProduct3DPreview__tabs">
            {editorTabs.map((tab) => (
                <button
                    type="button"
                    key={tab.value}
                    className={activeTab === tab.value ? 'is-active' : ''}
                    data-admin-sync-id={`admin-3d-editor-tab-${tab.value}`}
                    onClick={() => setActiveTab(tab.value)}
                >
                    {tab.icon}
                    <span>{tab.label}</span>
                </button>
            ))}
        </div>
    );

    const renderProductPricePanel = () => {
        const calcForProductRule = (rule) => calcProductPrice(
            {
                ...productPricing,
                enabled: true,
                sizeRules: [],
            },
            rule,
            product?.price,
            {
                blankPriceCatalogs,
                millingPricing,
                millingFallbackPrice: form.workPrice,
                pricingPolicy,
                workOptions: effectiveWorkOptions,
            }
        );
        const volumeMm3 = (dimensions = {}) => (
            toNumber(dimensions.height) *
            toNumber(dimensions.width) *
            toNumber(dimensions.thickness)
        );
        const formatCalcNumber = (value, digits = 3) => {
            const number = Number(value);
            if (!Number.isFinite(number)) return '0';

            return number.toLocaleString('ru-RU', {
                minimumFractionDigits: digits,
                maximumFractionDigits: digits,
            });
        };

        const basePrice = toNumber(productPricing.basePrice);
        const baseVolume = volumeMm3(productPricing.baseDimensions);
        const currentVolume = volumeMm3(productTestDimensions);
        const rawVolumeRatio = baseVolume > 0 ? currentVolume / baseVolume : 1;
        const safeVolumeRatio = Math.max(1, rawVolumeRatio || 1);
        const increasePercent = safeVolumeRatio > 1
            ? Math.min(300, Math.max(0, toNumber(productPricing.setupPercent)))
            : 0;
        const isBiggerThanBase = rawVolumeRatio > 1;
        const priceByVolume = basePrice * safeVolumeRatio;
        const markupValue = isBiggerThanBase
            ? priceByVolume * (increasePercent / 100)
            : 0;
        const priceBeforeRound = priceByVolume + markupValue;
        const roundTo = toNumber(productPricing.roundTo) || 100;
        const finalCoefficient = isBiggerThanBase
            ? safeVolumeRatio * (1 + increasePercent / 100)
            : 1;
        const baseVolumeM3 = baseVolume / 1000000000;
        const currentVolumeM3 = currentVolume / 1000000000;
        const exactRuleActive = productTestPreview.source === 'rule';
        const canExplainFormula = basePrice > 0 && baseVolume > 0 && currentVolume > 0;
        const calculatedBlankDimensions = resolveBlankDimensions(
            productTestDimensions,
            productPricing,
            pricingPolicy || {}
        );
        const selectedBlankMaterial = blankPriceCatalogs.find((item) => (
            item?.key === productPricing.blankMaterialKey
        )) || null;

        const isBlankCatalogOutdated = Boolean(
            selectedBlankMaterial
            && Number(productPricing.blankCatalogRevision || 0) < Number(selectedBlankMaterial.revision || 0)
        );
        const blankPickerCandidates = buildBlankPickerCandidates({
            catalog: selectedBlankMaterial,
            blankType: productPricing.blankType || 'stele',
            polishingType: productPricing.blankPolishingType || 'two-side',
            targetDimensions: calculatedBlankDimensions,
            query: blankPickerQuery,
            showAll: blankPickerShowAll,
        });

        const applyBlankPickerCandidate = (candidate) => {
            updateProductPricing({
                blankSizingMode: 'manual',
                manualBlankDimensions: {
                    height: candidate.dimensions.height,
                    width: candidate.dimensions.width,
                    thickness: candidate.dimensions.thickness,
                    unit: 'mm',
                },
                blankSupplierKey: candidate.offer.supplierKey || '',
                blankPriceOverride: 0,
                allowEstimatedBlankPrice: false,
            });
            setBlankPickerOpen(false);
        };

        const renderBlankPickerDialog = () => {
            if (!blankPickerOpen || typeof document === 'undefined') return null;

            return createPortal(
                <div
                    className="adminProduct3DPreview__blankPickerOverlay"
                    role="presentation"
                    onMouseDown={(event) => {
                        if (event.target === event.currentTarget) setBlankPickerOpen(false);
                    }}
                >
                    <section
                        className="adminProduct3DPreview__blankPickerDialog"
                        role="dialog"
                        aria-modal="true"
                        aria-label="Выбор заготовки"
                        onMouseDown={(event) => event.stopPropagation()}
                    >
                        <header>
                            <div>
                                <span>Справочник заготовок</span>
                                <strong>{selectedBlankMaterial?.name || 'Материал не выбран'}</strong>
                                <small>Нужно: {formatMillingDimensions(calculatedBlankDimensions)}</small>
                            </div>
                            <button
                                type="button"
                                onClick={() => setBlankPickerOpen(false)}
                                aria-label="Закрыть"
                            >×</button>
                        </header>

                        <div className="adminProduct3DPreview__blankPickerModes">
                            <button
                                type="button"
                                className={(productPricing.blankSizingMode || 'standard') === 'standard' ? 'is-active' : ''}
                                onClick={() => updateProductPricing({
                                    blankSizingMode: 'standard',
                                    blankPriceOverride: 0,
                                })}
                            >
                                Стандарт 50/10
                            </button>
                            <button
                                type="button"
                                className={productPricing.blankSizingMode === 'exact' ? 'is-active' : ''}
                                onClick={() => updateProductPricing({
                                    blankSizingMode: 'exact',
                                    blankPriceOverride: 0,
                                })}
                            >
                                Точный размер
                            </button>
                        </div>

                        <div className="adminProduct3DPreview__blankPickerTools">
                            <input
                                type="search"
                                value={blankPickerQuery}
                                onChange={(event) => setBlankPickerQuery(event.target.value)}
                                placeholder="Размер, поставщик или название"
                                autoFocus
                            />
                            <label>
                                <input
                                    type="checkbox"
                                    checked={blankPickerShowAll}
                                    onChange={(event) => setBlankPickerShowAll(event.target.checked)}
                                />
                                <span>Показать и меньшие</span>
                            </label>
                        </div>

                        <div className="adminProduct3DPreview__blankPickerList">
                            {blankPickerCandidates.length ? (
                                blankPickerCandidates.map((candidate) => (
                                    <article
                                        key={candidate.key}
                                        className={candidate.exact ? 'is-exact' : candidate.fits ? 'is-fit' : 'is-small'}
                                    >
                                        <div>
                                            <strong>{formatMillingDimensions(candidate.dimensions)}</strong>
                                            <span>
                                                {candidate.exact
                                                    ? 'Точное совпадение'
                                                    : candidate.fits
                                                        ? 'Подходит — заготовка не меньше изделия'
                                                        : 'Меньше нужного размера'}
                                            </span>
                                            <small>
                                                {candidate.offer.supplierKey
                                                    ? `Поставщик: ${candidate.offer.supplierKey}`
                                                    : candidate.rule?.label || 'Рабочая цена справочника'}
                                            </small>
                                        </div>
                                        <b>{fmtMoney(candidate.price)}</b>
                                        <button
                                            type="button"
                                            onClick={() => applyBlankPickerCandidate(candidate)}
                                            disabled={!candidate.fits}
                                        >
                                            Выбрать
                                        </button>
                                    </article>
                                ))
                            ) : (
                                <div className="adminProduct3DPreview__blankPickerEmpty">
                                    <strong>Подходящих заготовок с ценой нет</strong>
                                    <span>Включи «Показать и меньшие» для просмотра всего справочника или используй примерный расчёт.</span>
                                </div>
                            )}
                        </div>

                        <BlankPriceEstimator
                            key={[
                                selectedBlankMaterial?.key || 'no-material',
                                productPricing.blankType || 'stele',
                                productPricing.blankPolishingType || 'two-side',
                                calculatedBlankDimensions.height,
                                calculatedBlankDimensions.width,
                                calculatedBlankDimensions.thickness,
                            ].join(':')}
                            materialKey={selectedBlankMaterial?.key || ''}
                            materialName={selectedBlankMaterial?.name || ''}
                            initialBlankType={productPricing.blankType || 'stele'}
                            initialPolishingType={productPricing.blankPolishingType || 'two-side'}
                            initialSupplierKey={productPricing.blankSupplierKey || ''}
                            initialDimensions={calculatedBlankDimensions}
                            defaultOpen
                            compact
                            disabled={saving}
                            onUseEstimate={({ result, estimator }) => {
                                updateProductPricing({
                                    blankType: estimator.blankType,
                                    blankPolishingType: estimator.polishingType,
                                    blankSizingMode: 'manual',
                                    manualBlankDimensions: {
                                        height: estimator.height,
                                        width: estimator.width,
                                        thickness: estimator.thickness,
                                        unit: 'mm',
                                    },
                                    blankSupplierKey: estimator.supplierKey
                                        || result?.supplierKey
                                        || '',
                                    blankPriceOverride: 0,
                                    allowEstimatedBlankPrice: result?.estimated === true,
                                });
                            }}
                        />

                        <details className="adminProduct3DPreview__blankPickerManual">
                            <summary>Указать цену вручную</summary>
                            <div>
                                <input
                                    type="number"
                                    min="0"
                                    value={productPricing.blankPriceOverride || ''}
                                    onChange={(event) => updateProductPricing({
                                        blankPriceOverride: event.target.value,
                                    })}
                                    placeholder="Цена заготовки, ₽"
                                />
                                <button
                                    type="button"
                                    onClick={() => setBlankPickerOpen(false)}
                                    disabled={!(toNumber(productPricing.blankPriceOverride) > 0)}
                                >
                                    Применить
                                </button>
                            </div>
                        </details>
                    </section>
                </div>,
                document.body
            );
        };

        const renderPriceModeSelector = () => (
            <div className="adminProduct3DPreview__productModeSelector">
                <label>Как считать цену памятника</label>
                <select
                    value={productPricing.mode}
                    onChange={(event) => {
                        const nextMode = event.target.value;
                        const defaultMaterial = selectedBlankMaterial || blankPriceCatalogs.find((item) => item?.enabled !== false) || null;

                        updateProductPricing({
                            mode: nextMode,
                            enabled: true,
                            blankMaterialKey: productPricing.blankMaterialKey || defaultMaterial?.key || '',
                            blankMaterialName: productPricing.blankMaterialName || defaultMaterial?.name || '',
                            materialDensityKgM3: toNumber(productPricing.materialDensityKgM3) || toNumber(defaultMaterial?.densityKgM3) || 3070,
                            roundTo: toNumber(productPricing.roundTo) || toNumber(defaultMaterial?.defaultRoundTo) || 100,
                        });
                    }}
                    disabled={saving}
                >
                    {productPricingModeOptions.map((option) => (
                        <option key={option.value} value={option.value}>{option.label}</option>
                    ))}
                </select>
            </div>
        );

        const renderSharedDimensions = (layout = '') => (
            <>
                <div className={[
                    'adminProduct3DPreview__field',
                    layout === 'blankFormula' ? 'adminProduct3DPreview__field--blankDimension' : '',
                ].filter(Boolean).join(' ')}>
                    <label>Высота, мм</label>
                    <input
                        type="number"
                        min="0"
                        value={productTestDimensions.height}
                        onFocus={() => startProportionalEdit('product-test-dimensions', productTestDimensions)}
                        onChange={(event) => updateProductTestDimension('height', event.target.value)}
                        onBlur={() => finishMainDimensionEdit(
                            'product-test-dimensions',
                            'height',
                            productTestDimensions,
                            productPricing.baseDimensions,
                            updateProductTestDimension
                        )}
                        disabled={saving}
                    />
                </div>
                <div className={[
                    'adminProduct3DPreview__field',
                    layout === 'blankFormula' ? 'adminProduct3DPreview__field--blankDimension' : '',
                ].filter(Boolean).join(' ')}>
                    <label>Ширина, мм</label>
                    <input
                        type="number"
                        min="0"
                        value={productTestDimensions.width}
                        onFocus={() => startProportionalEdit('product-test-dimensions', productTestDimensions)}
                        onChange={(event) => updateProductTestDimension('width', event.target.value)}
                        onBlur={() => finishMainDimensionEdit(
                            'product-test-dimensions',
                            'width',
                            productTestDimensions,
                            productPricing.baseDimensions,
                            updateProductTestDimension
                        )}
                        disabled={saving}
                    />
                </div>
                <div className={[
                    'adminProduct3DPreview__field',
                    layout === 'blankFormula' ? 'adminProduct3DPreview__field--blankDimension' : '',
                ].filter(Boolean).join(' ')}>
                    <label>Толщина, мм</label>
                    <input
                        type="number"
                        min="0"
                        value={productTestDimensions.thickness}
                        onChange={(event) => updateProductTestDimension('thickness', event.target.value)}
                        onBlur={() => finishMainDimensionEdit(
                            'product-test-dimensions',
                            'thickness',
                            productTestDimensions,
                            productPricing.baseDimensions,
                            updateProductTestDimension
                        )}
                        disabled={saving}
                    />
                </div>
            </>
        );

        const renderProductSizeRulesPanel = () => (
                <details
                    className="adminProduct3DPreview__details"
                    open={productSizeRulesOpen}
                    onToggle={(event) => setProductSizeRulesOpen(event.currentTarget.open)}
                >
                    <summary>
                        Размеры изделия на сайте
                        <span>{productPricing.sizeRules.length}</span>
                    </summary>

                    <div className="adminProduct3DPreview__detailsBody">
                        <div className="adminProduct3DPreview__emptyBox">
                            Для каждого общего размера сохраняются две конечные цены. Этот же размер используется изделием и всеми включёнными работами.
                        </div>

                        <div className="adminProduct3DPreview__sizeRulesList">
                            {productPricing.sizeRules.length ? (
                                productPricing.sizeRules.map((rule, index) => {
                                    const rulePreview = calcForProductRule(rule);
                                    const ruleCustomerPrices = getCustomerPricesForPreview(rulePreview);
                                    const ruleWeightKg = rule.weightManual
                                        ? toNumber(rule.weightKg)
                                        : calcProductSizeWeightKg(rule, productPricing.materialDensityKgM3);

                                    return (
                                    <div className="adminProduct3DPreview__sizeRule" key={rule._clientId || index}>
                                        <div className="adminProduct3DPreview__sizeRuleHeader">
                                            <label>
                                                <input
                                                    type="checkbox"
                                                    checked={rule.enabled !== false}
                                                    onChange={(event) => updateProductSizeRule(index, 'enabled', event.target.checked)}
                                                    disabled={saving}
                                                />
                                                <span>
                                                    {formatMillingDimensions(rule)} · Подписка {fmtMoney(ruleCustomerPrices.subscriberPrice)} · Обычная {fmtMoney(ruleCustomerPrices.publicPrice)} · {fmtWeightKg(ruleWeightKg)}
                                                </span>
                                            </label>

                                            <div>
                                                <button type="button" onClick={() => showProductRuleOnScene(rule)} disabled={saving} title="Показать размер">↗</button>
                                                <button type="button" onClick={() => duplicateProductSizeRule(index)} disabled={saving} title="Дублировать">
                                                    <ContentCopyIcon fontSize="small" />
                                                </button>
                                                <button type="button" onClick={() => removeProductSizeRule(index)} disabled={saving} title="Удалить">
                                                    <DeleteOutlineIcon fontSize="small" />
                                                </button>
                                            </div>
                                        </div>

                                        <div className="adminProduct3DPreview__sizeRuleGrid adminProduct3DPreview__sizeRuleGrid--product">
                                            <div className="adminProduct3DPreview__field">
                                                <label>Высота</label>
                                                <input
                                                    type="number"
                                                    min="0"
                                                    value={rule.height}
                                                    onFocus={() => startProportionalEdit(`product-size-rule-${rule._clientId || index}`, rule)}
                                                    onChange={(event) => updateProductSizeRule(index, 'height', event.target.value)}
                                                    onBlur={() => stopProportionalEdit(`product-size-rule-${rule._clientId || index}`)}
                                                    disabled={saving}
                                                />
                                            </div>

                                            <div className="adminProduct3DPreview__field">
                                                <label>Ширина</label>
                                                <input
                                                    type="number"
                                                    min="0"
                                                    value={rule.width}
                                                    onFocus={() => startProportionalEdit(`product-size-rule-${rule._clientId || index}`, rule)}
                                                    onChange={(event) => updateProductSizeRule(index, 'width', event.target.value)}
                                                    onBlur={() => stopProportionalEdit(`product-size-rule-${rule._clientId || index}`)}
                                                    disabled={saving}
                                                />
                                            </div>

                                            <div className="adminProduct3DPreview__field">
                                                <label>Толщина</label>
                                                <input type="number" min="0" value={rule.thickness} onChange={(event) => updateProductSizeRule(index, 'thickness', event.target.value)} disabled={saving} />
                                            </div>

                                            <div className="adminProduct3DPreview__field adminProduct3DPreview__field--formulaReadonly adminProduct3DPreview__field--subscriberPrice">
                                                <label>Цена с подпиской</label>
                                                <input type="text" value={fmtMoney(ruleCustomerPrices.subscriberPrice)} readOnly />
                                            </div>

                                            <div className="adminProduct3DPreview__field adminProduct3DPreview__field--formulaReadonly adminProduct3DPreview__field--publicPrice">
                                                <label>Обычная цена</label>
                                                <input type="text" value={fmtMoney(ruleCustomerPrices.publicPrice)} readOnly />
                                            </div>

                                            <div className="adminProduct3DPreview__field adminProduct3DPreview__field--formulaReadonly">
                                                <label>Работы</label>
                                                <input type="text" value={fmtMoney(rulePreview.worksPrice || rulePreview.millingPrice)} readOnly />
                                            </div>

                                            <div className="adminProduct3DPreview__field">
                                                <label>Вес, кг</label>
                                                <input type="number" min="0" step="0.1" value={rule.weightKg} onChange={(event) => updateProductSizeRule(index, 'weightKg', event.target.value)} disabled={saving} />
                                            </div>

                                            <label className="adminProduct3DPreview__miniCheck">
                                                <input type="checkbox" checked={!!rule.weightManual} onChange={(event) => updateProductSizeRule(index, 'weightManual', event.target.checked)} disabled={saving} />
                                                <span>вес вручную</span>
                                            </label>
                                        </div>
                                    </div>
                                    );
                                })
                            ) : (
                                <div className="adminProduct3DPreview__emptyBox">
                                    Размеры изделия пока не добавлены. Выставь текущий размер выше и нажми «Добавить текущий размер на сайт».
                                </div>
                            )}
                        </div>
                    </div>
                </details>
        );

        if (productPricing.mode === 'blankPlusMilling') {
            return (
                <div className="adminProduct3DPreview__millingCard adminProduct3DPreview__millingCard--productPrice adminProduct3DPreview__millingCard--blankFormula">
                    <div className="adminProduct3DPreview__formTitle">
                        <strong>Цена памятника: заготовка + работы</strong>
                        <span>Выбери материал. Цена заготовки берётся из справочника, затем добавляются включённые работы и глобальная финансовая политика.</span>
                    </div>

                    <div className="adminProduct3DPreview__productPriceControls">
                        {renderPriceModeSelector()}

                        <label className="adminProduct3DPreview__switchRow adminProduct3DPreview__switchRow--automatic">
                            <span>
                                <strong>Автоматическая цена изделия</strong>
                                <small>Массовый пересчёт изменяет только товары с этим режимом.</small>
                            </span>
                            <input
                                type="checkbox"
                                checked={!!productPricing.enabled}
                                onChange={(event) => updateProductPricing({ enabled: event.target.checked })}
                                disabled={saving}
                            />
                        </label>

                        {renderProportionalResizeSwitch()}
                    </div>

                    <div className="adminProduct3DPreview__simpleGroup adminProduct3DPreview__simpleGroup--oneFormula">
                        <div className="adminProduct3DPreview__simpleGroupHead">
                            <strong>Размер, заготовка и итоговая цена</strong>
                        </div>

                        <div className="adminProduct3DPreview__compactGrid adminProduct3DPreview__compactGrid--blankFormula">
                            {renderSharedDimensions('blankFormula')}

                            <div className="adminProduct3DPreview__field adminProduct3DPreview__field--wide">
                                <label>Размер заготовки</label>
                                <select
                                    value={productPricing.blankSizingMode || 'standard'}
                                    onChange={(event) => updateProductPricing({ blankSizingMode: event.target.value })}
                                    disabled={saving}
                                >
                                    {blankSizingModeOptions.map((option) => (
                                        <option key={option.value} value={option.value}>{option.label}</option>
                                    ))}
                                </select>
                            </div>

                            {productPricing.blankSizingMode === 'manual' ? (
                                <>
                                    {['height', 'width', 'thickness'].map((key) => (
                                        <div className="adminProduct3DPreview__field adminProduct3DPreview__field--blankDimension" key={`manual-blank-${key}`}>
                                            <label>{key === 'height' ? 'Заготовка: высота, мм' : key === 'width' ? 'Заготовка: ширина, мм' : 'Заготовка: толщина, мм'}</label>
                                            <input
                                                type="number"
                                                min="1"
                                                value={productPricing.manualBlankDimensions?.[key] || ''}
                                                onChange={(event) => updateProductPricing({
                                                    manualBlankDimensions: {
                                                        ...(productPricing.manualBlankDimensions || {}),
                                                        [key]: event.target.value,
                                                    },
                                                })}
                                                disabled={saving}
                                            />
                                        </div>
                                    ))}
                                </>
                            ) : null}

                            <div className="adminProduct3DPreview__field adminProduct3DPreview__field--formulaReadonly adminProduct3DPreview__field--blankHalf">
                                <label>Размер изделия</label>
                                <input type="text" value={formatMillingDimensions(productTestDimensions)} readOnly />
                            </div>

                            <div className="adminProduct3DPreview__field adminProduct3DPreview__field--formulaReadonly adminProduct3DPreview__field--blankHalf">
                                <label>Размер заготовки</label>
                                <input type="text" value={formatMillingDimensions(calculatedBlankDimensions)} readOnly />
                            </div>

                            <div className="adminProduct3DPreview__field adminProduct3DPreview__field--blankPolishing adminProduct3DPreview__field--blankHalf">
                                <label>Полировка заготовки</label>
                                <select
                                    value={productPricing.blankPolishingType || 'two-side'}
                                    onChange={(event) => updateProductPricing({ blankPolishingType: event.target.value })}
                                    disabled={saving}
                                >
                                    {blankPolishingOptions.map((option) => (
                                        <option key={option.value} value={option.value}>{option.label}</option>
                                    ))}
                                </select>
                            </div>

                            <div
                                className={[
                                    'adminProduct3DPreview__field',
                                    'adminProduct3DPreview__field--blankHalf',
                                    'adminProduct3DPreview__field--blankPricePicker',
                                    productTestPreview.blankPrice > 0 ? 'is-ready' : 'is-missing',
                                    productTestPreview.blankEstimated ? 'is-estimated' : '',
                                ].filter(Boolean).join(' ')}
                            >
                                <label>Заготовка</label>
                                <div className="adminProduct3DPreview__blankPickerInline">
                                    <input
                                        type="text"
                                        value={productTestPreview.blankPrice > 0
                                            ? formatCalculatedBlankPrice(productTestPreview)
                                            : 'Нет подходящей заготовки'}
                                        readOnly
                                        title={productTestPreview.blankPrice > 0
                                            ? productTestPreview.blankEstimated
                                                ? 'Цена рассчитана приблизительно'
                                                : 'Точная цена из справочника'
                                            : 'Открой список заготовок и выбери похожую либо включи примерный расчёт'}
                                    />
                                    <button
                                        type="button"
                                        className="adminProduct3DPreview__blankPickerButton"
                                        onClick={() => {
                                            setBlankPickerQuery('');
                                            setBlankPickerShowAll(false);
                                            setBlankPickerOpen(true);
                                        }}
                                        disabled={saving || !selectedBlankMaterial}
                                        title="Открыть список заготовок"
                                        aria-label="Открыть список заготовок"
                                    >
                                        <svg viewBox="0 0 24 24" aria-hidden="true">
                                            <rect x="3" y="4" width="18" height="16" rx="2" />
                                            <path d="M7 8h10M7 12h10M7 16h6" />
                                        </svg>
                                    </button>
                                </div>
                                {productTestPreview.blankReferenceMaterialName && productTestPreview.blankReferenceBasePrice > 0 ? (
                                    <small className="adminProduct3DPreview__priceSourceNote is-accent">
                                        {productTestPreview.blankReferenceMaterialName}: {fmtMoney(productTestPreview.blankReferenceBasePrice)}
                                        {' × '}{Number(productTestPreview.blankReferenceMultiplier || 1).toFixed(2)}
                                        {' = '}{formatCalculatedBlankPrice(productTestPreview)} · страховочная оценка
                                    </small>
                                ) : productTestPreview.blankPrice > 0 ? (
                                    <small className="adminProduct3DPreview__priceSourceNote">
                                        {productTestPreview.blankEstimated
                                            ? 'Предварительная цена из справочника заготовок.'
                                            : 'Подтверждённая цена из справочника заготовок.'}
                                    </small>
                                ) : null}
                            </div>

                            <div className="adminProduct3DPreview__field adminProduct3DPreview__field--formulaReadonly adminProduct3DPreview__field--blankHalf">
                                <label>Работы</label>
                                <input type="text" value={fmtMoney(productTestPreview.worksPrice || productTestPreview.millingPrice)} readOnly />
                            </div>

                            <div className="adminProduct3DPreview__field adminProduct3DPreview__field--formulaReadonly adminProduct3DPreview__field--blankHalf">
                                <label>Вес сейчас</label>
                                <input type="text" value={fmtWeightKg(productTestPreview.weightKg)} readOnly />
                            </div>
                        </div>

                        <PricingPolicyInlineBreakdown
                            pricingPolicy={pricingPolicy}
                            blankPrice={productTestPreview.blankPrice}
                            millingPrice={productTestPreview.worksPrice || productTestPreview.millingPrice}
                        />

                        <div className="adminProduct3DPreview__rulesToolbar">
                            <button type="button" onClick={resetProductTestToStandard} disabled={saving}>Вернуть базовый размер</button>
                            <button type="button" onClick={setProductTestAsStandard} disabled={saving || !hasCompleteDimensions(productTestDimensions)}>Сделать текущий размер базовым</button>
                            <button type="button" onClick={addProductTestSizeToList} disabled={saving || !hasCompleteDimensions(productTestDimensions)}>
                                <AddIcon fontSize="small" /> Добавить размер на сайт
                            </button>
                        </div>
                    </div>

                    {renderProductSizeRulesPanel()}
                    {renderBlankPickerDialog()}
                </div>
            );
        }

        if (productPricing.mode === 'manual') {
            return (
                <div className="adminProduct3DPreview__millingCard adminProduct3DPreview__millingCard--productPrice">
                    <div className="adminProduct3DPreview__formTitle">
                        <strong>Ручная цена памятника целиком</strong>
                        <span>Цена вводится напрямую. Справочник заготовок и массовый пересчёт этот товар не изменят.</span>
                    </div>

                    <label className="adminProduct3DPreview__switchRow">
                        <span><strong>Показывать цену изделия</strong><small>Сохраняется как основная цена товара.</small></span>
                        <input type="checkbox" checked={!!productPricing.enabled} onChange={(event) => updateProductPricing({ enabled: event.target.checked })} disabled={saving} />
                    </label>

                    {renderPriceModeSelector()}

                    <div className="adminProduct3DPreview__simpleGroup adminProduct3DPreview__simpleGroup--oneFormula">
                        <div className="adminProduct3DPreview__compactGrid">
                            {renderSharedDimensions()}
                            <div className="adminProduct3DPreview__field adminProduct3DPreview__field--wide">
                                <label>Цена памятника целиком, ₽</label>
                                <input
                                    type="number"
                                    min="0"
                                    value={productPricing.manualPrice}
                                    onChange={(event) => updateProductPricing({ manualPrice: event.target.value, basePrice: event.target.value })}
                                    disabled={saving}
                                />
                            </div>
                            <div className="adminProduct3DPreview__field">
                                <label>Вес сейчас</label>
                                <input type="text" value={fmtWeightKg(productTestPreview.weightKg)} readOnly />
                            </div>
                            <div className="adminProduct3DPreview__field">
                                <label>Цена сейчас</label>
                                <input type="text" value={fmtMoney(productTestPreview.price)} readOnly />
                            </div>
                        </div>

                        <div className="adminProduct3DPreview__rulesToolbar">
                            <button type="button" onClick={setProductTestAsStandard} disabled={saving || !hasCompleteDimensions(productTestDimensions)}>Сделать текущий размер базовым</button>
                            <button type="button" onClick={addProductTestSizeToList} disabled={saving || !hasCompleteDimensions(productTestDimensions)}>
                                <AddIcon fontSize="small" /> Добавить размер на сайт
                            </button>
                        </div>
                    </div>

                    {renderProductSizeRulesPanel()}
                </div>
            );
        }

        return (
            <div className="adminProduct3DPreview__millingCard adminProduct3DPreview__millingCard--productPrice">
                <div className="adminProduct3DPreview__formTitle">
                    <strong>Изделие целиком: расчёт по объёму</strong>
                    <span>
                        Цена не уменьшается ниже базы. При увеличении размера применяется отдельная наценка этого камня — на вес, погрузку, перемещение и доставку.
                    </span>
                </div>

                {renderPriceModeSelector()}

                <label className="adminProduct3DPreview__switchRow">
                    <span>
                        <strong>Расчёт цены изделия по формуле</strong>
                        <small>Если выключено — используется обычная основная цена товара.</small>
                    </span>

                    <input
                        type="checkbox"
                        checked={!!productPricing.enabled}
                        onChange={(event) => updateProductPricing({ enabled: event.target.checked })}
                        disabled={saving}
                    />
                </label>

                {renderProportionalResizeSwitch()}

                <div className="adminProduct3DPreview__simpleGroup adminProduct3DPreview__simpleGroup--oneFormula">
                    <div className="adminProduct3DPreview__simpleGroupHead">
                        <strong>Размер и расчёт изделия</strong>
                    </div>

                    <div className="adminProduct3DPreview__compactGrid">
                        <div className="adminProduct3DPreview__field">
                            <label>Высота, мм</label>
                            <input
                                type="number"
                                min="0"
                                value={productTestDimensions.height}
                                onFocus={() => startProportionalEdit('product-test-dimensions', productTestDimensions)}
                                onChange={(event) => updateProductTestDimension('height', event.target.value)}
                                onBlur={() => finishMainDimensionEdit(
                                    'product-test-dimensions',
                                    'height',
                                    productTestDimensions,
                                    productPricing.baseDimensions,
                                    updateProductTestDimension
                                )}
                                disabled={saving}
                            />
                        </div>

                        <div className="adminProduct3DPreview__field">
                            <label>Ширина, мм</label>
                            <input
                                type="number"
                                min="0"
                                value={productTestDimensions.width}
                                onFocus={() => startProportionalEdit('product-test-dimensions', productTestDimensions)}
                                onChange={(event) => updateProductTestDimension('width', event.target.value)}
                                onBlur={() => finishMainDimensionEdit(
                                    'product-test-dimensions',
                                    'width',
                                    productTestDimensions,
                                    productPricing.baseDimensions,
                                    updateProductTestDimension
                                )}
                                disabled={saving}
                            />
                        </div>

                        <div className="adminProduct3DPreview__field">
                            <label>Толщина, мм</label>
                            <input
                                type="number"
                                min="0"
                                value={productTestDimensions.thickness}
                                onChange={(event) => updateProductTestDimension('thickness', event.target.value)}
                                onBlur={() => finishMainDimensionEdit(
                                    'product-test-dimensions',
                                    'thickness',
                                    productTestDimensions,
                                    productPricing.baseDimensions,
                                    updateProductTestDimension
                                )}
                                disabled={saving}
                            />
                        </div>

                        <div className="adminProduct3DPreview__field">
                            <label>Цена базы, ₽</label>
                            <input
                                type="number"
                                min="0"
                                value={productPricing.basePrice}
                                onChange={(event) => updateProductPricing({ basePrice: event.target.value })}
                                disabled={saving}
                            />
                        </div>

                        <div className="adminProduct3DPreview__field">
                            <label>Наценка при увеличении, %</label>
                            <input
                                type="number"
                                min="0"
                                max="300"
                                value={productPricing.setupPercent}
                                onChange={(event) => updateProductPricing({ setupPercent: event.target.value })}
                                disabled={saving}
                            />
                        </div>

                        <div className="adminProduct3DPreview__field">
                            <label>Округление</label>
                            <select
                                value={productPricing.roundTo}
                                onChange={(event) => updateProductPricing({ roundTo: event.target.value })}
                                disabled={saving}
                            >
                                {millingRoundOptions.map((option) => (
                                    <option key={option.value} value={option.value}>
                                        {option.label}
                                    </option>
                                ))}
                            </select>
                        </div>

                        <div className="adminProduct3DPreview__field">
                            <label>Плотность, кг/м³</label>
                            <input
                                type="number"
                                min="0"
                                value={productPricing.materialDensityKgM3}
                                onChange={(event) => updateProductPricing({ materialDensityKgM3: event.target.value })}
                                disabled={saving}
                            />
                        </div>

                        <div className="adminProduct3DPreview__field">
                            <label>Вес сейчас</label>
                            <input type="text" value={fmtWeightKg(productTestPreview.weightKg)} readOnly />
                        </div>

                        <div className="adminProduct3DPreview__field">
                            <label>Цена сейчас</label>
                            <input type="text" value={fmtMoney(productTestPreview.price)} readOnly />
                        </div>

                        <div className="adminProduct3DPreview__field adminProduct3DPreview__field--formulaReadonly">
                            <label>Формула</label>
                            <input type="text" value="База × объём + % только вверх" readOnly />
                        </div>
                    </div>

                    <div className="adminProduct3DPreview__formulaStats adminProduct3DPreview__formulaStats--wide">
                        <div>
                            <span>Базовый размер</span>
                            <strong>{formatMillingDimensions(productPricing.baseDimensions)}</strong>
                        </div>
                        <div>
                            <span>Объём базы</span>
                            <strong>{formatCalcNumber(baseVolumeM3, 4)} м³</strong>
                        </div>
                        <div>
                            <span>Объём размера</span>
                            <strong>{formatCalcNumber(currentVolumeM3, 4)} м³</strong>
                        </div>
                        <div>
                            <span>Объёмный коэфф.</span>
                            <strong>{safeVolumeRatio.toFixed(3)}</strong>
                        </div>
                        <div>
                            <span>Наценка изделия</span>
                            <strong>{isBiggerThanBase ? `+${increasePercent}%` : '0%'}</strong>
                        </div>
                        <div>
                            <span>Итоговый коэфф.</span>
                            <strong>{finalCoefficient.toFixed(3)}</strong>
                        </div>
                    </div>

                    <div className={`adminProduct3DPreview__calcBreakdown ${productBreakdownOpen ? 'is-open' : ''}`}>
                        <button
                            type="button"
                            className="adminProduct3DPreview__calcBreakdownHead"
                            onClick={() => setProductBreakdownOpen((prev) => !prev)}
                        >
                            <span>Почему получилось {fmtMoney(productTestPreview.price)}</span>
                            <em>{productBreakdownOpen ? 'Свернуть' : 'Показать расчёт'}</em>
                        </button>

                        {productBreakdownOpen ? (
                            <div className="adminProduct3DPreview__calcBreakdownBody">
                                {exactRuleActive ? (
                                    <div className="adminProduct3DPreview__calcBreakdownWarning">
                                        Для этого размера найдена точная ручная цена «{productTestPreview.rule?.label}». Она имеет приоритет над формулой.
                                    </div>
                                ) : null}

                                {!canExplainFormula ? (
                                    <div className="adminProduct3DPreview__calcBreakdownWarning">
                                        Заполни базовый размер, текущий размер и цену базы. Пока один из параметров равен нулю, нормальный расчёт невозможен.
                                    </div>
                                ) : null}

                                <div>
                                    <span>1.</span>
                                    <p>
                                        База: {formatMillingDimensions(productPricing.baseDimensions)} = {formatCalcNumber(baseVolumeM3, 4)} м³. Цена базы: {fmtMoney(basePrice)}.
                                    </p>
                                </div>
                                <div>
                                    <span>2.</span>
                                    <p>
                                        Текущий размер: {formatMillingDimensions(productTestDimensions)} = {formatCalcNumber(currentVolumeM3, 4)} м³. Вес: {fmtWeightKg(productTestPreview.weightKg)}.
                                    </p>
                                </div>
                                <div>
                                    <span>3.</span>
                                    <p>
                                        Объёмный коэффициент: {formatCalcNumber(currentVolumeM3, 4)} / {formatCalcNumber(baseVolumeM3, 4)} = {rawVolumeRatio.toFixed(3)}. {rawVolumeRatio < 1 ? 'Размер меньше базы, поэтому для цены берём 1.000 и цену не уменьшаем.' : `Для расчёта берём ${safeVolumeRatio.toFixed(3)}.`}
                                    </p>
                                </div>
                                <div>
                                    <span>4.</span>
                                    <p>
                                        Цена по объёму: {fmtMoney(basePrice)} × {safeVolumeRatio.toFixed(3)} = {fmtMoney(priceByVolume)}.
                                    </p>
                                </div>
                                <div>
                                    <span>5.</span>
                                    <p>
                                        {isBiggerThanBase
                                            ? `Наценка изделия ${increasePercent}% учитывает больший вес, погрузку, перемещение и доставку: +${fmtMoney(markupValue)}.`
                                            : `Размер не больше базы, поэтому наценка изделия ${toNumber(productPricing.setupPercent)}% не применяется.`}
                                    </p>
                                </div>
                                <div>
                                    <span>6.</span>
                                    <p>
                                        До округления: {fmtMoney(priceByVolume)} + {fmtMoney(markupValue)} = {fmtMoney(priceBeforeRound)}. Округление до {roundTo} ₽ → {fmtMoney(Math.round(priceBeforeRound / roundTo) * roundTo)}.
                                    </p>
                                </div>
                                <div>
                                    <span>7.</span>
                                    <p>
                                        Итог: {exactRuleActive
                                            ? `используется точная цена размера ${fmtMoney(productTestPreview.price)}.`
                                            : `${fmtMoney(productTestPreview.price)} при коэффициенте ${finalCoefficient.toFixed(3)}.`}
                                    </p>
                                </div>
                            </div>
                        ) : null}
                    </div>

                    <div className="adminProduct3DPreview__rulesToolbar">
                        <button type="button" onClick={resetProductTestToStandard} disabled={saving}>
                            Вернуть базовый размер
                        </button>

                        <button type="button" onClick={setProductTestAsStandard} disabled={saving || !hasCompleteDimensions(productTestDimensions)}>
                            Сделать базой и добавить на сайт
                        </button>

                        <button type="button" onClick={addProductTestSizeToList} disabled={saving || !hasCompleteDimensions(productTestDimensions)}>
                            <AddIcon fontSize="small" />
                            Добавить текущий размер на сайт
                        </button>
                    </div>
                </div>

                {renderProductSizeRulesPanel()}
            </div>
        );
    };

    const renderWorkPricePanel = () => {
        const calcForRule = (rule) => calcMillingPrice(
            {
                ...millingPricing,
                enabled: true,
                sizeRules: [],
            },
            rule,
            form.workPrice,
            pricingPolicy || {}
        );
        const calcWeightForRule = (rule) => calcProductSizeWeightKg(
            rule,
            productPricing.materialDensityKgM3
        );

        const isMillingWork = form.workType === 'milling';
        const rawCurrentWorkPreview = calcWorkOptionPrice(
            effectiveWorkOptions.find((item) => String(item.key) === String(form.workKey)) || {
                key: form.workKey,
                name: form.workName,
                enabled: form.workEnabled,
                workType: form.workType,
                calcMode: form.workCalcMode,
                price: form.workPrice,
                pricePerMeter: form.workPricePerMeter,
                pricePerSquareMeter: form.workPricePerSquareMeter,
                pricePerCubicMeter: form.workPricePerCubicMeter,
                pricing: millingPricing,
            },
            testDimensions,
            pricingPolicy || {}
        );
        const currentWorkPreview = isMillingWork
            ? applyMaterialMillingMarkup(
                rawCurrentWorkPreview,
                activeBlankMaterialCatalog,
                pricingPolicy || {}
            )
            : rawCurrentWorkPreview;

        const basePrice = toNumber(millingPricing.basePrice);
        const rawAreaRatio = Number(testPreview.rawAreaRatio || testPreview.areaRatio || 1);
        const safeAreaRatio = Number(testPreview.areaRatio || 1);
        const thicknessSteps = Number(testPreview.thicknessSteps || 0);
        const thicknessStepMm = Number(testPreview.thicknessStepMm || millingPricing.thicknessStepMm || 10);
        const thicknessMarkupPercent = Number(testPreview.thicknessMarkupPercent || millingPricing.thicknessMarkupPercent || 0);
        const thicknessMarkupTotalPercent = Number(testPreview.thicknessMarkupTotalPercent || 0);
        const priceByArea = basePrice * safeAreaRatio;
        const thicknessMarkupValue = priceByArea * (thicknessMarkupTotalPercent / 100);
        const priceBeforeMaterialMarkup = priceByArea + thicknessMarkupValue;
        const materialMillingMarkupPercent = Number(testPreview.materialMarkupPercent || 0);
        const materialMillingMarkupValue = priceBeforeMaterialMarkup * (materialMillingMarkupPercent / 100);
        const priceBeforeRound = priceBeforeMaterialMarkup + materialMillingMarkupValue;
        const roundTo = toNumber(pricingPolicy?.roundTo) || toNumber(millingPricing.roundTo) || 100;
        const baseAreaM2 = Number(testPreview.baseArea || 0) / 1000000;
        const currentAreaM2 = Number(testPreview.currentArea || 0) / 1000000;
        const finalCoefficient = basePrice > 0 ? Number(testPreview.price || 0) / basePrice : Number(testPreview.ratio || 1);
        const canExplainFormula = basePrice > 0 && baseAreaM2 > 0 && currentAreaM2 > 0;

        const formatCalcNumber = (value, digits = 3) => {
            const number = Number(value);
            if (!Number.isFinite(number)) return '0';
            return number.toLocaleString('ru-RU', {
                minimumFractionDigits: digits,
                maximumFractionDigits: digits,
            });
        };

        return (
            <div className="adminProduct3DPreview__millingCard">
                <div className="adminProduct3DPreview__formTitle">
                    <strong>Работы по общему размеру изделия</strong>
                </div>

                <div className="adminProduct3DPreview__workManager">
                    <div className="adminProduct3DPreview__field adminProduct3DPreview__field--wide">
                        <label>Текущая работа</label>
                        <select value={String(form.workKey || '')} onChange={(event) => selectWorkOption(event.target.value)} disabled={saving}>
                            {effectiveWorkOptions.map((work) => (
                                <option key={work.key} value={work.key}>
                                    {work.name || 'Работа'}{work.enabled === false ? ' · выкл.' : ''}
                                </option>
                            ))}
                        </select>
                    </div>
                    <button type="button" onClick={addCustomWorkOption} disabled={saving}>
                        <AddIcon fontSize="small" /> Добавить работу
                    </button>
                    <button type="button" className="is-danger" onClick={removeCurrentWorkOption} disabled={saving || effectiveWorkOptions.length === 0}>
                        <DeleteOutlineIcon fontSize="small" /> Удалить
                    </button>
                </div>

                <label className="adminProduct3DPreview__switchRow">
                    <span>
                        <strong>Показывать работу на сайте</strong>
                    </span>

                    <input
                        type="checkbox"
                        checked={!!form.workEnabled}
                        onChange={(event) => onChange('workEnabled', event.target.checked)}
                        disabled={saving}
                    />
                </label>

                <div className="adminProduct3DPreview__compactGrid adminProduct3DPreview__compactGrid--workIdentity">
                    <div className="adminProduct3DPreview__field">
                        <label>Название работы</label>
                        <input
                            type="text"
                            value={form.workName || ''}
                            placeholder="Например: Фрезеровка"
                            onChange={(event) => onChange('workName', event.target.value)}
                            disabled={saving}
                        />
                    </div>

                    <div className="adminProduct3DPreview__field">
                        <label>Тип работы</label>
                        <select value={form.workType || 'milling'} onChange={(event) => onChange('workType', event.target.value)} disabled={saving}>
                            {workTypeOptions.map((option) => (
                                <option key={option.value} value={option.value}>{option.label}</option>
                            ))}
                        </select>
                    </div>

                    <div className="adminProduct3DPreview__field">
                        <label>Расчёт работы</label>
                        <select value={form.workCalcMode || 'fixed'} onChange={(event) => onChange('workCalcMode', event.target.value)} disabled={saving}>
                            {calcModeOptions.map((option) => (
                                <option key={option.value} value={option.value}>{option.label}</option>
                            ))}
                        </select>
                    </div>

                    {isMillingWork ? (
                        <div className="adminProduct3DPreview__field adminProduct3DPreview__field--formulaReadonly">
                            <label>Цена фрезеровки базового размера клиенту</label>
                            <input
                                type="text"
                                value={fmtMoney(toNumber(millingPricing.basePrice) || toNumber(form.workPrice))}
                                readOnly
                            />
                            <small className="adminProduct3DPreview__priceSourceNote">
                                Конечная цена базового размера для {canonicalMillingMaterialName}; для других камней применяется их коэффициент сложности.
                            </small>
                        </div>
                    ) : (
                        <div className="adminProduct3DPreview__field">
                            <label>{activeCalcMode.label}</label>
                            <input type="number" min="0" value={form[activeCalcMode.field]} onChange={(event) => onChange(activeCalcMode.field, event.target.value)} disabled={saving} />
                        </div>
                    )}

                    <div className="adminProduct3DPreview__field adminProduct3DPreview__field--wide">
                        <label>Комментарий / действие</label>
                        <input
                            type="text"
                            value={form.workNote || ''}
                            placeholder="Например: доп. ручная обработка"
                            onChange={(event) => onChange('workNote', event.target.value)}
                            disabled={saving}
                        />
                    </div>
                </div>

                <label className="adminProduct3DPreview__switchRow">
                    <span>
                        <strong>{isMillingWork ? 'Фрезеровка по формуле' : 'Цены работы по общим размерам'}</strong>
                    </span>

                    <input
                        type="checkbox"
                        checked={!!millingPricing.enabled}
                        onChange={(event) => updateMillingPricing({ enabled: event.target.checked })}
                        disabled={saving || !form.workEnabled}
                    />
                </label>

                {renderProportionalResizeSwitch()}

                <div className="adminProduct3DPreview__simpleGroup adminProduct3DPreview__simpleGroup--oneFormula">
                    <div className="adminProduct3DPreview__simpleGroupHead">
                        <strong>Размер и расчёт</strong>
                    </div>

                    <div className="adminProduct3DPreview__rulesToolbar">
                        <button
                            type="button"
                            onClick={copyProductBaseToMilling}
                            disabled={saving || !hasCompleteDimensions(productPricing.baseDimensions)}
                            title={`Копировать ${formatMillingDimensions(productPricing.baseDimensions)}. Цена фрезеровки не изменится.`}
                        >
                            <ContentCopyIcon fontSize="small" />
                            Показать базовый общий размер
                        </button>
                    </div>

                    <div className="adminProduct3DPreview__compactGrid">
                        <div className="adminProduct3DPreview__field">
                            <label>Высота, мм</label>
                            <input
                                type="number"
                                min="0"
                                value={testDimensions.height}
                                onFocus={() => startProportionalEdit('work-test-dimensions', testDimensions)}
                                onChange={(event) => updateTestDimension('height', event.target.value)}
                                onBlur={() => finishMainDimensionEdit(
                                    'work-test-dimensions',
                                    'height',
                                    testDimensions,
                                    millingPricing.baseDimensions,
                                    updateTestDimension
                                )}
                                disabled={saving}
                            />
                        </div>

                        <div className="adminProduct3DPreview__field">
                            <label>Ширина, мм</label>
                            <input
                                type="number"
                                min="0"
                                value={testDimensions.width}
                                onFocus={() => startProportionalEdit('work-test-dimensions', testDimensions)}
                                onChange={(event) => updateTestDimension('width', event.target.value)}
                                onBlur={() => finishMainDimensionEdit(
                                    'work-test-dimensions',
                                    'width',
                                    testDimensions,
                                    millingPricing.baseDimensions,
                                    updateTestDimension
                                )}
                                disabled={saving}
                            />
                        </div>

                        <div className="adminProduct3DPreview__field">
                            <label>Толщина, мм</label>
                            <input
                                type="number"
                                min="0"
                                value={testDimensions.thickness}
                                onChange={(event) => updateTestDimension('thickness', event.target.value)}
                                onBlur={() => finishMainDimensionEdit(
                                    'work-test-dimensions',
                                    'thickness',
                                    testDimensions,
                                    millingPricing.baseDimensions,
                                    updateTestDimension
                                )}
                                disabled={saving}
                            />
                        </div>

                        {isMillingWork ? (
                            <>
                        <div className="adminProduct3DPreview__field">
                            <label>Цена клиенту базовой фрезеровки ({canonicalMillingMaterialName}), ₽</label>
                            <input
                                type="number"
                                min="0"
                                value={millingPricing.basePrice}
                                onChange={(event) => updateMillingPricing({ basePrice: event.target.value })}
                                disabled={saving || !isCanonicalMillingMaterial}
                            />
                            <small className="adminProduct3DPreview__priceSourceNote">
                                {isCanonicalMillingMaterial
                                    ? 'Вводится конечная цена работы для клиента. Она сразу применяется ко всем материалам как база расчёта размера.'
                                    : `Редактируется на ${canonicalMillingMaterialName}; здесь показана клиентская база до +${Number(activeBlankMaterialCatalog?.millingMarkupPercent || 0)}% за сложность материала.`}
                            </small>
                        </div>

                        <label className="adminProduct3DPreview__miniCheck adminProduct3DPreview__miniCheck--wide">
                            <input
                                type="checkbox"
                                checked={millingPricing.useGlobalThicknessPricing !== false}
                                onChange={(event) => updateMillingPricing({ useGlobalThicknessPricing: event.target.checked })}
                                disabled={saving}
                            />
                            <span>Брать надбавку толщины из глобальной ценовой политики</span>
                        </label>

                        <div className="adminProduct3DPreview__field">
                            <label>Шаг толщины, мм</label>
                            <input
                                type="number"
                                min="1"
                                value={millingPricing.thicknessStepMm}
                                onChange={(event) => updateMillingPricing({ thicknessStepMm: event.target.value })}
                                disabled={saving || millingPricing.useGlobalThicknessPricing !== false}
                            />
                        </div>

                        <div className="adminProduct3DPreview__field">
                            <label>Надбавка за шаг, %</label>
                            <input
                                type="number"
                                min="0"
                                max="100"
                                value={millingPricing.thicknessMarkupPercent}
                                onChange={(event) => updateMillingPricing({ thicknessMarkupPercent: event.target.value })}
                                disabled={saving || millingPricing.useGlobalThicknessPricing !== false}
                            />
                        </div>

                        <div className="adminProduct3DPreview__field">
                            <label>Максимальная надбавка, %</label>
                            <input
                                type="number"
                                min="0"
                                max="500"
                                value={millingPricing.thicknessMarkupMaxPercent}
                                onChange={(event) => updateMillingPricing({ thicknessMarkupMaxPercent: event.target.value })}
                                disabled={saving || millingPricing.useGlobalThicknessPricing !== false}
                            />
                        </div>

                        <div className="adminProduct3DPreview__field">
                            <label>Округление</label>
                            <select
                                value={millingPricing.roundTo}
                                onChange={(event) => updateMillingPricing({ roundTo: event.target.value })}
                                disabled={saving}
                            >
                                {millingRoundOptions.map((option) => (
                                    <option key={option.value} value={option.value}>
                                        {option.label}
                                    </option>
                                ))}
                            </select>
                        </div>

                            </>
                        ) : null}

                        <div className="adminProduct3DPreview__field adminProduct3DPreview__field--formulaReadonly">
                            <label>Цена сейчас</label>
                            <input
                                type="text"
                                value={fmtMoney(currentWorkPreview.price)}
                                readOnly
                            />
                            {isMillingWork ? (
                                <small className="adminProduct3DPreview__priceSourceNote is-accent">
                                    {fmtMoney(toNumber(testPreview.basePriceBeforeMaterialMarkup) || toNumber(testPreview.price))}
                                    {' + '}{Number(testPreview.materialMarkupPercent || 0)}% за {activeMaterialDisplayName}
                                    {' = '}{fmtMoney(testPreview.price)}
                                </small>
                            ) : null}
                        </div>

                        {['blankPlusMilling', 'manual'].includes(productPricing.mode) ? (
                            <div className="adminProduct3DPreview__field adminProduct3DPreview__field--formulaReadonly adminProduct3DPreview__field--finalProductPrice">
                                <label>Обычная цена</label>
                                <input type="text" value={fmtMoney(productTestCustomerPrices.publicPrice)} title="Конечная цена по глобальной политике" readOnly />
                            </div>
                        ) : null}
                        {['blankPlusMilling', 'manual'].includes(productPricing.mode) ? (
                            <div className="adminProduct3DPreview__field adminProduct3DPreview__field--formulaReadonly adminProduct3DPreview__field--subscriberPrice">
                                <label>Цена с подпиской</label>
                                <input type="text" value={fmtMoney(productTestCustomerPrices.subscriberPrice)} readOnly />
                            </div>
                        ) : null}

                        <div className="adminProduct3DPreview__field adminProduct3DPreview__field--formulaReadonly">
                            <label>Вес заготовки сейчас</label>
                            <input
                                type="text"
                                value={fmtWeightKg(millingCurrentWeightKg)}
                                title="Считается по текущему размеру и плотности из вкладки «Изделие целиком»"
                                readOnly
                            />
                        </div>

                        {isMillingWork ? (
                        <div className="adminProduct3DPreview__field adminProduct3DPreview__field--formulaReadonly">
                            <label>Формула</label>
                            <input
                                type="text"
                                value="Цена базового размера × площадь + надбавка за толщину"
                                readOnly
                            />
                        </div>
                        ) : null}
                    </div>

                    {isMillingWork ? (
                        <div className="adminProduct3DPreview__formulaStats adminProduct3DPreview__formulaStats--wide">
                            <div><span>Площадь базы</span><strong>{formatCalcNumber(baseAreaM2, 3)} м²</strong></div>
                            <div><span>Площадь размера</span><strong>{formatCalcNumber(currentAreaM2, 3)} м²</strong></div>
                            <div><span>Коэфф. площади</span><strong>{safeAreaRatio.toFixed(3)}</strong></div>
                            <div><span>Шаги толщины</span><strong>{thicknessSteps} × {thicknessStepMm} мм</strong></div>
                            <div><span>Надбавка толщины</span><strong>+{thicknessMarkupTotalPercent}%</strong></div>
                            <div><span>Материал</span><strong>+{materialMillingMarkupPercent}%</strong></div>
                            <div><span>Итоговый коэфф.</span><strong>{finalCoefficient.toFixed(3)}</strong></div>
                        </div>
                    ) : null}

                    <PricingPolicyInlineBreakdown
                        pricingPolicy={pricingPolicy}
                        blankPrice={productTestPreview.blankPrice}
                        millingPrice={currentWorkPreview.price}
                        mode="milling"
                    />

                    {isMillingWork ? (
                    <div className={`adminProduct3DPreview__calcBreakdown ${workBreakdownOpen ? 'is-open' : ''}`}>
                        <button
                            type="button"
                            className="adminProduct3DPreview__calcBreakdownHead"
                            onClick={() => setWorkBreakdownOpen((prev) => !prev)}
                        >
                            <span>Почему получилось {fmtMoney(testPreview.price)}</span>
                            <em>{workBreakdownOpen ? 'Свернуть' : 'Показать расчёт'}</em>
                        </button>

                        {workBreakdownOpen ? (
                            <div className="adminProduct3DPreview__calcBreakdownBody">
                                {!canExplainFormula ? (
                                    <div className="adminProduct3DPreview__calcBreakdownWarning">
                                        Заполни базовый и проверяемый размер, а также цену базы.
                                    </div>
                                ) : null}
                                <div><span>1.</span><p>Базовый размер: {formatMillingDimensions(millingPricing.baseDimensions)}. Площадь {formatCalcNumber(baseAreaM2, 3)} м², конечная цена работы клиенту {fmtMoney(basePrice)}.</p></div>
                                <div><span>2.</span><p>Текущий размер: {formatMillingDimensions(testDimensions)}. Площадь {formatCalcNumber(currentAreaM2, 3)} м².</p></div>
                                <div><span>3.</span><p>Коэффициент площади: max(1, {rawAreaRatio.toFixed(3)}) = {safeAreaRatio.toFixed(3)}. Уменьшение ниже базы не удешевляет работу.</p></div>
                                <div><span>4.</span><p>Толщина выше базы даёт {thicknessSteps} шаг(а) по {thicknessStepMm} мм × {thicknessMarkupPercent}% = +{thicknessMarkupTotalPercent}%.</p></div>
                                <div><span>5.</span><p>{fmtMoney(basePrice)} × {safeAreaRatio.toFixed(3)} + {fmtMoney(thicknessMarkupValue)} = {fmtMoney(priceBeforeMaterialMarkup)} до коэффициента материала.</p></div>
                                <div><span>6.</span><p>Наценка материала +{materialMillingMarkupPercent}% = {fmtMoney(materialMillingMarkupValue)}. Округление до {roundTo} ₽ → {fmtMoney(testPreview.price)}.</p></div>
                            </div>
                        ) : null}

                    </div>

                    ) : null}
                    <div className="adminProduct3DPreview__rulesToolbar">
                        <button type="button" onClick={resetTestToStandard} disabled={saving}>
                            Вернуть базовый размер
                        </button>

                        <button type="button" onClick={setTestAsStandard} disabled={saving || !hasCompleteDimensions(testDimensions)}>
                            Сделать общим базовым размером
                        </button>

                        <button type="button" onClick={addTestSizeToList} disabled={saving || !hasCompleteDimensions(testDimensions)}>
                            <AddIcon fontSize="small" />
                            Добавить общий размер
                        </button>
                    </div>
                </div>

                <details className="adminProduct3DPreview__details">
                    <summary>Оси модели</summary>

                    <div className="adminProduct3DPreview__simpleGroup">
                        <div className="adminProduct3DPreview__simpleGroupHead">
                            <strong>Оси GLB</strong>
                        </div>

                        <div className="adminProduct3DPreview__field">
                            <label>Тип осей GLB</label>
                            <select
                                value={millingPricing.axisPreset}
                                onChange={(event) => updateMillingPricing({ axisPreset: event.target.value })}
                                disabled={saving}
                            >
                                {axisPresetOptions.map((option) => (
                                    <option key={option.value} value={option.value}>
                                        {option.label}
                                    </option>
                                ))}
                            </select>
                        </div>
                    </div>
                </details>

                <div className="adminProduct3DPreview__simpleGroup adminProduct3DPreview__sharedWorkSizes">
                    <div className="adminProduct3DPreview__simpleGroupHead">
                        <strong>Общие размеры и цена текущей работы</strong>
                        <span>«Убрать из работы» отключает только текущую работу. «Удалить общий размер» убирает размер также у изделия и других работ.</span>
                    </div>

                    <div className="adminProduct3DPreview__rulesToolbar">
                        <button type="button" onClick={addProductTestSizeToList} disabled={saving || !hasCompleteDimensions(testDimensions)}>
                            <AddIcon fontSize="small" /> Добавить общий размер
                        </button>
                    </div>

                    <div className="adminProduct3DPreview__sizeRulesList">
                        {productPricing.sizeRules.length ? (
                            productPricing.sizeRules.map((sharedRule, index) => {
                                const workRule = getCurrentWorkRule(sharedRule);
                                const rawWorkPreview = calcWorkOptionPrice({
                                    key: form.workKey,
                                    name: form.workName,
                                    enabled: form.workEnabled,
                                    workType: form.workType,
                                    calcMode: form.workCalcMode,
                                    price: form.workPrice,
                                    pricePerMeter: form.workPricePerMeter,
                                    pricePerSquareMeter: form.workPricePerSquareMeter,
                                    pricePerCubicMeter: form.workPricePerCubicMeter,
                                    pricing: {
                                        ...millingPricing,
                                        sizeRules: workRule ? [workRule] : [],
                                    },
                                }, sharedRule, pricingPolicy || {});
                                const workPreview = isMillingWork
                                    ? applyMaterialMillingMarkup(
                                        rawWorkPreview,
                                        activeBlankMaterialCatalog,
                                        pricingPolicy || {}
                                    )
                                    : rawWorkPreview;

                                return (
                                    <div className="adminProduct3DPreview__sizeRule" key={sharedRule.sizeKey || sharedRule._clientId || index}>
                                        <div className="adminProduct3DPreview__sizeRuleHeader">
                                            <label>
                                                <input
                                                    type="checkbox"
                                                    checked={sharedRule.enabled !== false && workRule?.enabled !== false}
                                                    readOnly
                                                    aria-label="Размер доступен для текущей работы"
                                                />
                                                <span>
                                                    {formatMillingDimensions(sharedRule)}
                                                    {sharedRule.isBase ? ' · БАЗОВЫЙ' : ''}
                                                    {workRule?.enabled === false
                                                        ? ` · ${form.workName || 'Работа'} отключена`
                                                        : ` · ${form.workName || 'Работа'} ${fmtMoney(workPreview.price)}`}
                                                </span>
                                            </label>
                                            <div style={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 6 }}>
                                                <button
                                                    type="button"
                                                    onClick={() => showRuleOnScene(sharedRule)}
                                                    disabled={saving}
                                                    title="Показать размер в 3D"
                                                    aria-label="Показать размер в 3D"
                                                >↗</button>
                                                <button
                                                    type="button"
                                                    onClick={() => setCurrentWorkSizeEnabled(sharedRule, workRule?.enabled === false)}
                                                    disabled={saving || sharedRule.enabled === false}
                                                    title={workRule?.enabled === false
                                                        ? 'Вернуть размер в текущую работу'
                                                        : 'Убрать размер только из текущей работы, оставив изделие'}
                                                >
                                                    {workRule?.enabled === false ? 'Вернуть в работу' : 'Убрать из работы'}
                                                </button>
                                                <button
                                                    type="button"
                                                    onClick={() => removeProductSizeRule(index)}
                                                    disabled={saving}
                                                    title="Удалить общий размер из изделия и всех работ"
                                                    aria-label={`Удалить общий размер ${formatMillingDimensions(sharedRule)} везде`}
                                                >
                                                    <DeleteOutlineIcon fontSize="small" />
                                                </button>
                                            </div>
                                        </div>

                                        <div className="adminProduct3DPreview__sizeRuleGrid adminProduct3DPreview__sizeRuleGrid--sharedWork">
                                            <div className="adminProduct3DPreview__field adminProduct3DPreview__field--formulaReadonly">
                                                <label>Общий размер</label>
                                                <input type="text" value={formatMillingDimensions(sharedRule)} readOnly />
                                            </div>
                                            {isMillingWork && !isCanonicalMillingMaterial ? (
                                                <div className="adminProduct3DPreview__field adminProduct3DPreview__field--formulaReadonly">
                                                    <label>Цена для {activeMaterialDisplayName}, ₽</label>
                                                    <input type="text" value={fmtMoney(workPreview.price)} readOnly />
                                                    <small className="adminProduct3DPreview__priceSourceNote">
                                                        База {fmtMoney(toNumber(rawWorkPreview.price))} + {Number(activeBlankMaterialCatalog?.millingMarkupPercent || 0)}% за материал.
                                                    </small>
                                                </div>
                                            ) : (
                                                <div className="adminProduct3DPreview__field">
                                                    <label>{isMillingWork ? `Базовая цена (${canonicalMillingMaterialName}), ₽` : 'Цена работы для размера, ₽'}</label>
                                                    <input
                                                        type="number"
                                                        min="0"
                                                        value={workRule?.price || ''}
                                                        placeholder={String(Math.round(toNumber(workPreview.price) || 0))}
                                                        onChange={(event) => updateCurrentWorkSizePrice(sharedRule, event.target.value)}
                                                        disabled={saving || workRule?.enabled === false}
                                                    />
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                );
                            })
                        ) : (
                            <div className="adminProduct3DPreview__emptyBox">
                                Общих размеров пока нет. Выставь размер выше и нажми «Добавить общий размер».
                            </div>
                        )}
                    </div>
                </div>


            </div>
        );
    };

    const getGraniteTabContext = () => {
        const variants = Array.isArray(form.materialPricingVariants) ? form.materialPricingVariants : [];
        const activeMaterialKey = form.activeMaterialKey
            || form.defaultMaterialKey
            || productPricing.blankMaterialKey
            || 'gabbro-diabase';

        return { variants, activeMaterialKey };
    };

    const renderGraniteTab = () => {
        const { variants, activeMaterialKey } = getGraniteTabContext();

        return (
            <div className="adminProduct3DPreview__tabPanel">
                <AdminMaterialVariantSelector
                    catalogs={blankPriceCatalogs}
                    variants={variants}
                    activeMaterialKey={activeMaterialKey}
                    defaultMaterialKey={form.defaultMaterialKey}
                    modelMaterials={modelMaterials}
                    surfaceFinishPolicy={form.surfaceFinishPolicy}
                    priceSummaryByKey={materialPriceSummaryByKey}
                    saving={saving}
                    onSelectVariant={onSelectMaterialVariant}
                    onAddVariant={onAddMaterialVariant}
                    onSetDefaultVariant={onSetDefaultMaterialVariant}
                    onRemoveVariant={onRemoveMaterialVariant}
                    onChangeVariants={(nextVariants) => onChange('materialPricingVariants', nextVariants)}
                    onChangeSurfaceFinishPolicy={(nextPolicy) => onChange('surfaceFinishPolicy', nextPolicy)}
                />
            </div>
        );
    };

    const renderPricesTab = () => {
        return (
        <div className="adminProduct3DPreview__tabPanel">
            <div className="adminProduct3DPreview__priceModeSwitch">
                <button
                    type="button"
                    className={priceModeTab === 'product' ? 'is-active' : ''}
                    onClick={() => setPriceModeTab('product')}
                    disabled={saving}
                >
                    <strong>Изделие целиком</strong>
                    <span>Продажа товара: {productPricing.sizeRules.length} разм.</span>
                </button>

                <button
                    type="button"
                    className={priceModeTab === 'milling' ? 'is-active' : ''}
                    onClick={() => setPriceModeTab('milling')}
                    disabled={saving}
                >
                    <strong>Работы</strong>
                    <span>
                        {form.workName || 'Фрезеровка'} · {form.workEnabled
                            ? `${productPricing.sizeRules.length} общ. разм.`
                            : 'выключено'}
                    </span>
                </button>
            </div>

            {priceModeTab === 'product' ? renderProductPricePanel() : renderWorkPricePanel()}
        </div>
        );
    };

    const renderTagsTab = () => (
        <div className="adminProduct3DPreview__tabPanel">
            <div className="adminProduct3DPreview__formCard">
                <div className="adminProduct3DPreview__formTitle">
                    <strong>Хештеги товара</strong>
                    <span>
                        Нужны для поиска: роза, крест, свеча, дерево, арка и так далее.
                    </span>
                </div>

                <div className="adminProduct3DPreview__field">
                    <label>Быстрые теги</label>
                    <div className="adminProduct3DPreview__presetTags">
                        {tagPresets.map((preset) => (
                            <button
                                type="button"
                                key={preset.label}
                                onClick={() => onAddTagPreset(preset.values)}
                                disabled={saving}
                            >
                                {preset.label}
                            </button>
                        ))}
                    </div>
                </div>

                <div className="adminProduct3DPreview__field">
                    <label>Хештеги</label>
                    <textarea
                        value={form.hashtags}
                        placeholder="роза, крест, свеча, памятник с вазой"
                        onChange={(event) => onChange('hashtags', event.target.value)}
                        disabled={saving}
                    />
                    <small>
                        Разделяй теги запятой, точкой с запятой или новой строкой. Символ # можно не писать.
                    </small>
                </div>
            </div>
        </div>
    );

    const renderMainTab = () => (
        <div className="adminProduct3DPreview__tabPanel">
            <div className="adminProduct3DPreview__meta">
                <div>
                    <span>Название</span>
                    <b>{getProductName(product)}</b>
                </div>

                <div>
                    <span>Артикул</span>
                    <b>{product.article || '—'}</b>
                </div>

                <div>
                    <span>Категория</span>
                    <b>{product.categoryName || product.categorySlug || '—'}</b>
                </div>

                <div>
                    <span>Наличие</span>
                    <b>{Number(product.quantity || 0) > 0 ? 'В наличии' : 'Под заказ'}</b>
                </div>

                <div>
                    <span>Файл модели</span>
                    <b>{product.modelPath ? 'Подключён' : 'Не указан'}</b>
                </div>
            </div>

            <div className="adminProduct3DPreview__sceneCard">
                <div className="adminProduct3DPreview__formTitle">
                    <strong>Тип выбора материала на FRONT</strong>
                    <span>
                        Гранит показывает один переключатель породы и меняет
                        только разрешённые полированные поверхности. Стандарт
                        оставляет обычный выбор цвета/текстуры материалов.
                    </span>
                </div>

                <div className="adminProduct3DPreview__field">
                    <label>Режим материала</label>
                    <select
                        value={form.materialSelectionMode || 'standard'}
                        onChange={(event) => onChange(
                            'materialSelectionMode',
                            event.target.value
                        )}
                        disabled={saving}
                        data-admin-sync-id="admin-3d-material-selection-mode"
                    >
                        {materialSelectionModeOptions.map((option) => (
                            <option key={option.value} value={option.value}>
                                {option.label}
                            </option>
                        ))}
                    </select>
                </div>
            </div>

            <div className="adminProduct3DPreview__dimensionsCard">
                <div className="adminProduct3DPreview__formTitle">
                    <strong>Исходный размер модели</strong>
                    <span>
                        Размер из 3D-файла или карточки товара.
                    </span>
                </div>

                <div className="adminProduct3DPreview__dimensionGrid">
                    {realDimensionRows.map((row) => (
                        <div className="adminProduct3DPreview__dimensionItem" key={row.key}>
                            <span>{row.label}</span>
                            <b>{row.value}</b>
                        </div>
                    ))}
                </div>
            </div>

            <div className="adminProduct3DPreview__dimensionsCard">
                <div className="adminProduct3DPreview__formTitle">
                    <strong>Размер на сцене</strong>
                    <span>
                        Это текущий проверяемый размер.
                    </span>
                </div>

                <div className="adminProduct3DPreview__dimensionGrid">
                    {sceneDimensionRows.map((row) => (
                        <div className="adminProduct3DPreview__dimensionItem" key={row.key}>
                            <span>{row.label}</span>
                            <b>{row.value}</b>
                        </div>
                    ))}
                </div>

                <label className="adminProduct3DPreview__dimensionToggle">
                    <input
                        type="checkbox"
                        checked={!!showDimensions}
                        onChange={(event) => onToggleDimensions(event.target.checked)}
                    />
                    <span>Показывать размерные линии</span>
                </label>
            </div>
        </div>
    );

    const renderSettingsTab = () => (
        <div className="adminProduct3DPreview__tabPanel">
            <div className="adminProduct3DPreview__sceneCard">
                <div className="adminProduct3DPreview__formTitle">
                    <strong>Разбор 3D-модели</strong>
                    <span>
                        Включай только для моделей, которые специально
                        подготовлены для корректного разделения на детали.
                    </span>
                </div>

                <label className="adminProduct3DPreview__switchRow">
                    <span>
                        <strong>Разрешить разбор модели</strong>
                        <small>
                            По умолчанию выключено. После включения в
                            калькуляторе появится действие «Состав».
                        </small>
                    </span>

                    <input
                        type="checkbox"
                        checked={form.modelPartsEnabled === true}
                        onChange={(event) => onChange(
                            'modelPartsEnabled',
                            event.target.checked
                        )}
                        disabled={saving}
                    />
                </label>
            </div>

            <div className="adminProduct3DPreview__sceneCard">
                <div className="adminProduct3DPreview__sceneCardHeader">
                    <div className="adminProduct3DPreview__formTitle">
                        <strong>Качество сцены и линейка</strong>
                        <span>
                            Свет и фон теперь находятся прямо на 3D-сцене
                            в двух круглых кнопках. Их настройки сохраняются
                            автоматически для этого браузера.
                        </span>
                    </div>

                    <button
                        type="button"
                        className="adminProduct3DPreview__sceneReset"
                        onClick={resetSceneSettingsToDefault}
                        disabled={saving || sceneSettingsAreDefault}
                        title="Вернуть качество и размер линейки по умолчанию"
                    >
                        <RestartAltIcon fontSize="small" />
                        <span>По умолчанию</span>
                    </button>
                </div>

                <div className="adminProduct3DPreview__rangeField">
                    <label>
                        <span>Размер цифр линейки</span>
                        <strong>
                            {Number(
                                safeSceneSettings.dimensionLabelScale
                            ).toFixed(2)}
                            x
                        </strong>
                    </label>

                    <input
                        type="range"
                        min="0.8"
                        max="2.4"
                        step="0.05"
                        value={safeSceneSettings.dimensionLabelScale}
                        onChange={(event) => onSceneSettingChange(
                            'dimensionLabelScale',
                            Number(event.target.value)
                        )}
                    />
                </div>

                <div className="adminProduct3DPreview__field">
                    <label>Качество графики</label>
                    <select
                        value={safeSceneSettings.graphicsQuality}
                        onChange={(event) => onSceneSettingChange(
                            'graphicsQuality',
                            event.target.value
                        )}
                    >
                        {graphicsQualityOptions.map((option) => (
                            <option
                                key={option.value}
                                value={option.value}
                            >
                                {option.label}
                            </option>
                        ))}
                    </select>
                </div>
            </div>
        </div>
    );

    const renderActiveTab = () => {
        if (activeTab === 'granite') return renderGraniteTab();
        if (activeTab === 'tags') return renderTagsTab();
        if (activeTab === 'main') return renderMainTab();
        if (activeTab === 'settings') return renderSettingsTab();

        return renderPricesTab();
    };

    const activeGraniteContext = getGraniteTabContext();
    const activeGraniteCatalog = blankPriceCatalogs.find((catalog) => (
        catalog?.key === activeGraniteContext.activeMaterialKey
    ));
    const activeGraniteSummary = materialPriceSummaryByKey[activeGraniteContext.activeMaterialKey] || {};

    const topProductCustomerPrices = {
        // Для верхней карточки используем тот же текущий расчёт активного
        // гранита, что и rail в канвасе. Так здесь больше не появляется
        // старая техническая product.price / сумма до глобальной политики.
        subscriberPrice: Number(activeGraniteSummary?.subscriberPrice) > 0
            ? Number(activeGraniteSummary.subscriberPrice)
            : Number(productTestCustomerPrices?.subscriberPrice || 0),
        publicPrice: Number(activeGraniteSummary?.publicPrice) > 0
            ? Number(activeGraniteSummary.publicPrice)
            : Number(productTestCustomerPrices?.publicPrice || 0),
    };
    const topProductPriceMissing = (
        !(topProductCustomerPrices.subscriberPrice > 0)
        && !(topProductCustomerPrices.publicPrice > 0)
    );
    const topProductMetaText = `${formatMillingDimensions(productTestDimensions)} · ${fmtWeightKg(productTestPreview.weightKg)}`;
    const topProductPriceText = topProductPriceMissing
        ? `Цена не указана · ${topProductMetaText}`
        : `Подписка ${fmtMoney(topProductCustomerPrices.subscriberPrice)} · Обычная ${fmtMoney(topProductCustomerPrices.publicPrice)} · ${topProductMetaText}`;
    const topMillingPriceText = testPreview.source === 'none'
        ? `Цена не указана · ${formatMillingDimensions(testDimensions)}`
        : `${fmtMoney(testPreview.price)} · ${formatMillingDimensions(testDimensions)}`;

    const topPriceText = activeTab === 'prices'
        ? priceModeTab === 'product'
            ? topProductPriceText
            : topMillingPriceText
        : activeTab === 'granite'
            ? `${activeGraniteCatalog?.name || 'Гранит'} · изделие ${fmtMoney(activeGraniteSummary.productPrice)} · фрезеровка ${fmtMoney(activeGraniteSummary.millingPrice)}`
            : getMainPriceText(product);

    const topPriceLabel = activeTab === 'prices'
        ? priceModeTab === 'product'
            ? 'Цена изделия и вес'
            : 'Цена работы'
        : activeTab === 'granite'
            ? 'Выбранный вид гранита'
            : 'Цена товара';

    return (
        <aside
            className="adminProduct3DPreview__info"
            onKeyDown={preventUnsafeNumberKeys}
            onWheel={blurNumberInputOnWheel}
        >
            <div className="adminProduct3DPreview__stickyTop">
                {renderTabs()}

                <div className="adminProduct3DPreview__price">
                    <span>{topPriceLabel}</span>
                    {activeTab === 'prices' && priceModeTab === 'product' ? (
                        <div className="adminProduct3DPreview__priceDual">
                            {topProductPriceMissing ? (
                                <b className="adminProduct3DPreview__priceDualMissing">Цена не указана</b>
                            ) : (
                                <div className="adminProduct3DPreview__priceDualValues">
                                    <b>
                                        <small>Подписка</small>
                                        <strong>{fmtMoney(topProductCustomerPrices.subscriberPrice)}</strong>
                                    </b>
                                    <b>
                                        <small>Обычная</small>
                                        <strong>{fmtMoney(topProductCustomerPrices.publicPrice)}</strong>
                                    </b>
                                </div>
                            )}
                            <small className="adminProduct3DPreview__priceDualMeta">
                                {topProductMetaText}
                            </small>
                        </div>
                    ) : (
                        <b>{topPriceText}</b>
                    )}
                </div>
            </div>

            {renderActiveTab()}

            <div className="adminProduct3DPreview__stickyBottom">
                {saveStatus ? (
                    <div className={`adminProduct3DPreview__saveStatus is-${saveStatus.type}`}>
                        <strong>
                            {saveStatus.type === 'success' ? 'Сохранено успешно' : 'Ошибка сохранения'}
                        </strong>
                        <span>{saveStatus.text}</span>
                    </div>
                ) : isDirty ? (
                    <div className="adminProduct3DPreview__saveStatus is-pending">
                        <strong>Есть изменения</strong>
                        <span>Нажми “Сохранить”, чтобы записать настройки товара.</span>
                    </div>
                ) : (
                    <div className="adminProduct3DPreview__saveStatus is-clean">
                        <strong>Изменений нет</strong>
                        <span>Настройки товара сейчас сохранены.</span>
                    </div>
                )}

                <div className="adminProduct3DPreview__actions">
                    <button
                        type="button"
                        className="adminProduct3DPreview__secondaryBtn"
                        onClick={() => onReset(initialForm)}
                        disabled={saving || !isDirty}
                    >
                        Сбросить
                    </button>

                    <button
                        type="button"
                        className="adminProduct3DPreview__primaryBtn"
                        onClick={() => onSave()}
                        disabled={saving || !isDirty}
                    >
                        <SaveIcon fontSize="small" />
                        {saving ? 'Сохраняю...' : 'Сохранить'}
                    </button>
                </div>
            </div>
        </aside>
    );
};

export default AdminProduct3DInfoEditor;
